/*
 * ==========================================
 * IMPORTANT - NE PAS TRADUIRE / DO NOT TRANSLATE
 * ==========================================
 * 
 * Ce fichier est en FRANÇAIS uniquement.
 * This file must remain in FRENCH language only.
 * Este archivo debe permanecer en FRANCÉS únicamente.
 * 此文件必须保持法语。
 * 
 * Raison / Reason:
 * Application métier pour calculs de rémunération selon la législation française.
 * Business application for salary calculations under French tax law.
 * 
 * La traduction casserait:
 * - Les termes légaux français (charges sociales, IR, etc.)
 * - Les calculs basés sur le droit français
 * - La conformité réglementaire
 * 
 * Translation would break:
 * - French legal terminology
 * - Calculations based on French law  
 * - Regulatory compliance
 * ==========================================
 */

import React, { useEffect, useMemo, useRef, useState } from "react";

const TELEMETRY_ENDPOINT =
  window.location.hostname === "app.aimycore.fr"
    ? "https://api.aimycore.fr/api/telemetry/ingest"
    : "/api/telemetry/ingest";

// TÉLÉMÉTRIE AIMYCORE
const sendTelemetryPayload = (payload) => {
  fetch(TELEMETRY_ENDPOINT, {
    method: "POST",
    headers: {
      "Content-Type": "application/json"
    },
    credentials: "include",
    body: JSON.stringify(payload)
  }).catch(() => {
    // fail silently
  });
};

/* ========================= Helpers ========================= */

const clamp = (n, min, max) => Math.min(max, Math.max(min, n));

function toNumberLoose(str, fallback = 0) {
  if (str === null || str === undefined) return fallback;
  const s = String(str).trim().replace(/\s/g, "").replace(",", ".");
  if (s === "" || s === "-" || s === "." || s === "-.") return fallback;
  const n = Number(s);
  return Number.isFinite(n) ? n : fallback;
}

function fmtEur(n) {
  const v = Number.isFinite(n) ? n : 0;
  return v.toLocaleString("fr-FR", { style: "currency", currency: "EUR" });
}

function fmtPct(n, digits = 2) {
  const v = Number.isFinite(n) ? n : 0;
  return `${v.toLocaleString("fr-FR", { maximumFractionDigits: digits })} %`;
}

function fmtSigned(n, digits = 1) {
  const v = Number.isFinite(n) ? n : 0;
  const sign = v > 0 ? "+" : v < 0 ? "−" : "";
  const abs = Math.abs(v);
  return `${sign}${abs.toLocaleString("fr-FR", {
    maximumFractionDigits: digits,
    minimumFractionDigits: digits,
  })}`;
}

function commissionModeLabel(mode) {
  if (mode === "CA") return "Sur CA";
  if (mode === "MB") return "Sur Marge brute";
  if (mode === "MC") return "Sur Marge contributive";
  if (mode === "OVERRIDE_PCT") return "Override %";
  return mode || "Sur Marge brute";
}


function uid() {
  return `${Date.now()}_${Math.random().toString(16).slice(2)}`;
}

const LS_KEY = "remu_scenarios_v3";

/* =========================
   UI Components
========================= */

function Card({ title, subtitle, children, right }) {
  return (
    <div style={styles.card}>
      <div style={styles.cardHeader}>
        <div>
          <div style={styles.cardTitle}>{title}</div>
          {subtitle ? <div style={styles.cardSubtitle}>{subtitle}</div> : null}
        </div>
        {right ? <div>{right}</div> : null}
      </div>
      <div>{children}</div>
    </div>
  );
}

function Tabs({ tabs, value, onChange }) {
  return (
    <div style={styles.tabsRow}>
      {tabs.map((t) => {
        const active = t.value === value;
        return (
          <button
            key={t.value}
            onClick={() => onChange(t.value)}
            style={{
              ...styles.tabBtn,
              ...(active ? styles.tabBtnActive : {}),
            }}
            type="button"
          >
            {t.label}
          </button>
        );
      })}
    </div>
  );
}

function Badge({ tone = "neutral", children }) {
  const toneStyle =
    tone === "ok"
      ? styles.badgeOk
      : tone === "warn"
      ? styles.badgeWarn
      : tone === "bad"
      ? styles.badgeBad
      : styles.badgeNeutral;

  return <span style={{ ...styles.badge, ...toneStyle }}>{children}</span>;
}

function Toggle({ checked, onChange, label, hint }) {
  return (
    <div style={styles.toggleRow}>
      <label style={styles.toggleLabel}>
        <input
          type="checkbox"
          checked={!!checked}
          onChange={(e) => onChange(e.target.checked)}
        />
        <span style={{ marginLeft: 10 }}>{label}</span>
      </label>
      {hint ? <div style={styles.hint}>{hint}</div> : null}
    </div>
  );
}

function Select({ value, onChange, options }) {
  return (
    <select
      value={value}
      onChange={(e) => onChange(e.target.value)}
      style={styles.select}
    >
      {options.map((o) => (
        <option key={o.value} value={o.value}>
          {o.label}
        </option>
      ))}
    </select>
  );
}

/**
 * Input numérique en "text" pour éviter le bug “je n’arrive pas à effacer un chiffre”
 */
function NumInput({ value, onChange, suffix, placeholder, disabled = false }) {
  return (
    <div style={{ ...styles.inputWrap, opacity: disabled ? 0.7 : 1 }}>
      <input
        value={value}
        onChange={(e) => onChange(e.target.value)}
        style={{ ...styles.input, ...(disabled ? styles.inputDisabled : {}) }}
        type="text"
        inputMode="decimal"
        placeholder={placeholder || ""}
        spellCheck={false}
        disabled={disabled}
      />
      {suffix ? <span style={styles.suffix}>{suffix}</span> : null}
    </div>
  );
}

function Field({ label, hint, children }) {
  return (
    <div style={styles.field}>
      <div style={styles.fieldLabel}>{label}</div>
      {hint ? <div style={styles.hint}>{hint}</div> : null}
      <div style={{ marginTop: 8 }}>{children}</div>
    </div>
  );
}

function Table({ rows }) {
  return (
    <div style={styles.table}>
      {rows.map((r, idx) => (
        <div key={idx} style={styles.tableRow}>
          <div style={styles.tableKey}>{r.k}</div>
          <div style={styles.tableVal}>{r.v}</div>
        </div>
      ))}
    </div>
  );
}

function Divider() {
  return <div style={styles.divider} />;
}

/* =========================
   Core logic
========================= */

function computePlagesTotals(primeRanges, lastEndUserValue) {
  const rows = Array.isArray(primeRanges) ? primeRanges : [];
  const debugRows = [];

  let baseVolume = 0;
  let primeAmount = 0;

  for (let i = 0; i < rows.length; i += 1) {
    const row = rows[i] || {};
    const start = Math.max(0, toNumberLoose(row.start, 0));
    const ratePct = toNumberLoose(row.ratePct, 0);
    const enabled = row.enabled ?? true;
    const reached = row.reached ?? true;

    if (enabled === false) {
      debugRows.push({
        index: i,
        start,
        end: null,
        ratePct,
        enabled,
        reached,
        volume: 0,
        prime: 0,
        impact: false,
      });
      continue;
    }

    let end = null;
    if (i < rows.length - 1) {
      const nextStart = Math.max(0, toNumberLoose(rows[i + 1]?.start, 0));
      end = nextStart - 1;
    } else {
      const raw = String(lastEndUserValue ?? "").trim();
      if (raw !== "") {
        const parsedEnd = Number(raw.replace(/\s/g, "").replace(",", "."));
        end = Number.isFinite(parsedEnd) ? parsedEnd : null;
      }
    }

    let volume = 0;
    let prime = 0;
    let impact = false;

    if (!(end == null || end <= start)) {
      volume = end - start;
      prime = volume * (ratePct / 100);
      impact = reached === true;
    }

    if (impact) {
      baseVolume += volume;
      primeAmount += prime;
    }

    debugRows.push({
      index: i,
      start,
      end,
      ratePct,
      enabled,
      reached,
      volume,
      prime,
      impact,
    });
  }

  const totals = { baseVolume, primeAmount };
  return { totals, debugRows };
}

/**
 * Prime sur "objectifs" : si base >= objectif, alors prime += objectif * taux
 * (et palier 1 ne se déclenche que si base >= triggerStart)
 */
function computePrimeFromObjectives(base, triggerStart, objectives) {
  const b = Math.max(0, Number(base) || 0);
  const trigger = Math.max(0, Number(triggerStart) || 0);

  if (b < trigger) return 0;

  const cleaned = (objectives || [])
    .map((o) => {
      const objective = Math.max(0, Number(o.objective) || 0);
      const ratePct = Number(o.ratePct) || 0;
      return { objective, ratePct };
    })
    .filter((o) => Number.isFinite(o.objective))
    .sort((a, b) => a.objective - b.objective);

  let prime = 0;
  for (const o of cleaned) {
    if (b >= o.objective) {
      prime += o.objective * (o.ratePct / 100);
    }
  }
  return prime;
}


function commissionBaseLabel(mode) {
  if (mode === "CA") return "CA";
  if (mode === "MB") return "Marge brute";
  return "Marge contributive";
}

/* =========================
   Component
========================= */

export default function RemunerationSimu() {
  const DEBUG_PLAGES = false;
  const PLAGES_ENGINE_SIGNATURE = "computePlagesTotals";
  const DEBUG_PLAGES_SIGNATURE = "DEBUG_PLAGES";

  const [tab, setTab] = useState("FIXE");

  // ====== Bandeau (masqué par défaut)
  const [bannerOpen, setBannerOpen] = useState(false);

  // ====== Impression / export PDF
  const [printMode, setPrintMode] = useState(null); // null | "DEFAULT" | "COMPARE"
  const [printCompare, setPrintCompare] = useState(false); // compare 2 scénarios si possible
  const prevTabRef = useRef("FIXE");

  // ====== Fixe
  const [objectifBrutActuel, setObjectifBrutActuel] = useState("58000");
  const [salaireFixeBrut, setSalaireFixeBrut] = useState("48000");
  const [moisPresence, setMoisPresence] = useState("12");

  const [caN1, setCaN1] = useState("1030000");
  const [objectifAnneeN, setObjectifAnneeN] = useState("1500000"); // ✅ demandé :contentReference[oaicite:14]{index=14}

  // Charges / IR
  const [tauxChargesSalariales, setTauxChargesSalariales] = useState("22");
  const [tauxChargesPatronales, setTauxChargesPatronales] = useState("42"); // ✅ demandé :contentReference[oaicite:15]{index=15}
  const [tauxIR, setTauxIR] = useState("10");

  // Avantages en nature
  const [avantagesNature, setAvantagesNature] = useState("0");
  const [inclureAvantagesDansObjectif, setInclureAvantagesDansObjectif] =
    useState(false);

  // ====== Variable (commissions)
  const [modeCommission, setModeCommission] = useState("MB"); // CA / MB / MC

  // MB: taux standardisé (modifiable)
  const [tauxMargeBrute, setTauxMargeBrute] = useState("64"); // % (standard)

  // MC: soit ajustement en €, soit remplacement du taux de marge
  const [mcMode, setMcMode] = useState("EUR"); // EUR or OVERRIDE_PCT
  const [mcAdjEur, setMcAdjEur] = useState("0"); // € coûts directs
  const [mcOverridePct, setMcOverridePct] = useState("64"); // %

  const [caExistants, setCaExistants] = useState("1030000"); // ✅ Modif 8: valeur par défaut 1030000 :contentReference[oaicite:16]{index=16}
  const [caNouveaux, setCaNouveaux] = useState("300000"); // CA annuel (nouveaux clients)
  const [partHorsPerimetre, setPartHorsPerimetre] = useState("0"); // %

  // Commission taux
  const [tauxComExistants, setTauxComExistants] = useState("5");
  const [tauxComNouveaux, setTauxComNouveaux] = useState("7");

  // ====== Plancher de rémunération variable (commissions + prime) -> DÉPLACÉ DANS VARIABLE :contentReference[oaicite:17]{index=17}
  const [plancherVarOn, setPlancherVarOn] = useState(false);
  const [plancherVarVal, setPlancherVarVal] = useState("10000");
  const plancherTouchedRef = useRef(false);

  // ====== Bonus apporteurs d'affaires (nouveaux clients)
  // Idée : tu prends 10% si TU apportes le client. Si une partie du CA est apportée par d'autres,
  // tu ne prends qu'un bonus (par défaut 5%) sur cette part, et les apporteurs prennent leur part.
  const [apporteursEnabled, setApporteursEnabled] = useState(false);
  const [apporteursPeople, setApporteursPeople] = useState([
    { id: uid(), name: "Apporteur 1", sharePct: "100", ratePct: "5", baseCalc: "NOUVEAUX" },
  ]);

  // ====== Prime sur objectifs
  const [primeEnabled, setPrimeEnabled] = useState(true);
  const [basePrime, setBasePrime] = useState("CA"); // CA / MB / MC

  // Deux vues demandées :contentReference[oaicite:18]{index=18}
  const [primeView, setPrimeView] = useState("PLAGES"); // PLAGES / OBJECTIFS

  // Déclenchement palier 1 (vue Objectifs)
  const [primeTriggerStart, setPrimeTriggerStart] = useState(""); // auto = seuil1Default
  const triggerTouchedRef = useRef(false);

  // Vue "Plages de CA"
  const [primeRanges, setPrimeRanges] = useState([
    { id: uid(), start: "1500000", ratePct: "0.5", enabled: true, reached: true },
    { id: uid(), start: "1600001", ratePct: "1", enabled: true, reached: true },
    { id: uid(), start: "1800000", ratePct: "1.5", enabled: true, reached: false },
  ]);
  const [lastEndUserValue, setLastEndUserValue] = useState("");

  // Vue "Objectifs CA"
  const [primeObjectives, setPrimeObjectives] = useState([
    { id: uid(), objective: "1200000", ratePct: "0.5", enabled: true, reached: true },
    { id: uid(), objective: "1350000", ratePct: "1.0", enabled: true, reached: true },
    { id: uid(), objective: "1500000", ratePct: "1.5", enabled: true, reached: false },
  ]);
  // ====== Projection / comparaison
  const [compareMode, setCompareMode] = useState("ANNUEL"); // ANNUEL vs PERIODE
  const [scenarioName, setScenarioName] = useState("");
  const [scenarios, setScenarios] = useState([]);
  const [compareLeftId, setCompareLeftId] = useState("");
  const [compareRightId, setCompareRightId] = useState("");

  // ====== Slider
  const sliderMax = 10_000_000;
  const sliderStep = 1000;
  const sliderCaNew = useMemo(() => {
    const n = Math.round(toNumberLoose(caNouveaux, 0));
    return clamp(n, 0, sliderMax);
  }, [caNouveaux]);

  // ====== Load scenarios
  useEffect(() => {
    try {
      const raw = localStorage.getItem(LS_KEY);
      if (!raw) return;
      const parsed = JSON.parse(raw);
      if (Array.isArray(parsed)) {
        setScenarios(parsed);
        if (parsed[0]?.id) setCompareLeftId(parsed[0].id);
        if (parsed[1]?.id) setCompareRightId(parsed[1].id);
      }
    } catch {
      // ignore
    }
  }, []);

  // ====== Persist scenarios
  useEffect(() => {
    try {
      localStorage.setItem(LS_KEY, JSON.stringify(scenarios));
    } catch {
      // ignore
    }
  }, [scenarios]);

  // ====== TÉLÉMÉTRIE COMPORTEMENTALE ======

  const interactionsCountRef = useRef(0);
  const telemetryQueueRef = useRef([]);
  const telemetryLastSentAtRef = useRef(0);
  const telemetryFlushTimerRef = useRef(null);
  const snapshotDebounceTimerRef = useRef(null);

  const flushTelemetryQueue = () => {
    const queue = telemetryQueueRef.current || [];
    if (!queue.length) return;

    const now = Date.now();
    const elapsed = now - telemetryLastSentAtRef.current;
    const waitMs = Math.max(0, 2000 - elapsed); // throttle: 1 event / 2s

    if (waitMs > 0) {
      if (!telemetryFlushTimerRef.current) {
        telemetryFlushTimerRef.current = setTimeout(() => {
          telemetryFlushTimerRef.current = null;
          flushTelemetryQueue();
        }, waitMs);
      }
      return;
    }

    const nextPayload = queue.shift();
    telemetryQueueRef.current = queue;
    telemetryLastSentAtRef.current = Date.now();
    sendTelemetryPayload(nextPayload);

    if (telemetryQueueRef.current.length > 0 && !telemetryFlushTimerRef.current) {
      telemetryFlushTimerRef.current = setTimeout(() => {
        telemetryFlushTimerRef.current = null;
        flushTelemetryQueue();
      }, 2000);
    }
  };

  const enqueueTelemetry = (type, data) => {
    interactionsCountRef.current += 1;
    const payload = {
      type,
      data: {
        ...(data || {}),
        interactionsCount: interactionsCountRef.current,
      },
      timestamp: new Date().toISOString(),
    };
    telemetryQueueRef.current = [...(telemetryQueueRef.current || []), payload];
    flushTelemetryQueue();
  };

  const sendSnapshotDebounced = (data) => {
    if (snapshotDebounceTimerRef.current) clearTimeout(snapshotDebounceTimerRef.current);
    snapshotDebounceTimerRef.current = setTimeout(() => {
      enqueueTelemetry("simulation_snapshot", data);
    }, 800);
  };

  // Session ID unique pour corréler les événements d'une même visite
  const sessionIdRef = useRef(`${Date.now()}_${Math.random().toString(36).slice(2,8)}`);

  // 1. Connexion — une seule fois au montage
  useEffect(() => {
    enqueueTelemetry("connection", {
      sessionId: sessionIdRef.current,
      url: window.location.href,
      referrer: document.referrer,
      screenWidth: window.innerWidth,
      screenHeight: window.innerHeight,
      scenariosCount: 0 // sera mis à jour par le load
    });
  }, []);

  // 2. Changement de champ (debounced 1.5s pour ne pas spammer)
  const fieldChangeTimer = useRef(null);
  const sendFieldChange = (fieldName, value) => {
    clearTimeout(fieldChangeTimer.current);
    fieldChangeTimer.current = setTimeout(() => {
      enqueueTelemetry("field_change", {
        sessionId: sessionIdRef.current,
        tab,
        field: fieldName,
        value: String(value).slice(0, 50) // tronquer les valeurs longues
      });
    }, 1500);
  };

  // 3. Suivi du temps passé par onglet
  const tabEnteredAt = useRef(Date.now());
  const prevTab = useRef("FIXE");
  useEffect(() => {
    const now = Date.now();
    const duration = Math.round((now - tabEnteredAt.current) / 1000);
    // Envoyer le temps passé sur l'onglet précédent (si > 2s pour éviter le bruit)
    if (duration > 2 && prevTab.current !== tab) {
      enqueueTelemetry("tab_duration", {
        sessionId: sessionIdRef.current,
        tab: prevTab.current,
        durationSec: duration
      });
    }
    // Tracker le nouvel onglet
    if (prevTab.current !== tab) {
      enqueueTelemetry("tab_switch", {
        sessionId: sessionIdRef.current,
        from: prevTab.current,
        to: tab
      });
    }
    prevTab.current = tab;
    tabEnteredAt.current = now;
  }, [tab]);

  // 4. Déconnexion / fermeture de page
  useEffect(() => {
    const handleUnload = () => {
      const totalSec = Math.round((Date.now() - tabEnteredAt.current) / 1000);
      // Utilise sendBeacon pour garantir l'envoi même à la fermeture
      try {
        interactionsCountRef.current += 1;
        navigator.sendBeacon(TELEMETRY_ENDPOINT, JSON.stringify({
          type: "session_end",
          data: {
            sessionId: sessionIdRef.current,
            lastTab: tab,
            lastTabDurationSec: totalSec,
            totalPageDurationSec: Math.round((Date.now() - parseInt(sessionIdRef.current)) / 1000),
            interactionsCount: interactionsCountRef.current,
          },
          timestamp: new Date().toISOString()
        }));
      } catch { /* best effort */ }
    };
    window.addEventListener("beforeunload", handleUnload);
    return () => window.removeEventListener("beforeunload", handleUnload);
  }, [tab]);

  // 5. Tracking des modifications de champs clés (debounced, regroupe les changements)
  const pendingChanges = useRef({});
  const flushTimer = useRef(null);
  const flushFieldChanges = () => {
    const changes = pendingChanges.current;
    const keys = Object.keys(changes);
    if (keys.length === 0) return;
    enqueueTelemetry("fields_changed", {
      sessionId: sessionIdRef.current,
      tab,
      changes: { ...changes }
    });
    pendingChanges.current = {};
  };
  const trackField = (name, value) => {
    pendingChanges.current[name] = String(value).slice(0, 50);
    sendFieldChange(name, value);
    clearTimeout(flushTimer.current);
    flushTimer.current = setTimeout(flushFieldChanges, 2000);
  };

  // 6. Tracker les toggles et sélecteurs importants (envoi immédiat car action discrète)
  const trackToggle = (name, value) => {
    enqueueTelemetry("toggle_change", {
      sessionId: sessionIdRef.current,
      tab,
      toggle: name,
      value
    });
  };

  // 7. Snapshot périodique de l'état de la simulation (toutes les 30s si actif)
  const lastSnapshotRef = useRef(0);
  useEffect(() => {
    const interval = setInterval(() => {
      const now = Date.now();
      // Ne pas envoyer si inactif > 60s
      if (now - tabEnteredAt.current > 60000 && tab === prevTab.current) return;
      if (now - lastSnapshotRef.current < 25000) return;
      lastSnapshotRef.current = now;
      sendSnapshotDebounced({
        sessionId: sessionIdRef.current,
        tab,
        fixe: salaireFixeBrut,
        objectif: objectifBrutActuel,
        mois: moisPresence,
        caN1,
        objectifN: objectifAnneeN,
        modeCommission,
        caExistants,
        caNouveaux,
        tauxEx: tauxComExistants,
        tauxNew: tauxComNouveaux,
        primeEnabled,
        apporteursEnabled,
        plancherVarOn,
        scenariosCount: scenarios.length
      });
    }, 30000);
    return () => clearInterval(interval);
  }, [tab, salaireFixeBrut, objectifBrutActuel, moisPresence, caN1, objectifAnneeN,
      modeCommission, caExistants, caNouveaux, tauxComExistants, tauxComNouveaux,
      primeEnabled, apporteursEnabled, plancherVarOn, scenarios.length]);

  useEffect(() => {
    return () => {
      if (telemetryFlushTimerRef.current) clearTimeout(telemetryFlushTimerRef.current);
      if (snapshotDebounceTimerRef.current) clearTimeout(snapshotDebounceTimerRef.current);
      if (fieldChangeTimer.current) clearTimeout(fieldChangeTimer.current);
      if (flushTimer.current) clearTimeout(flushTimer.current);
    };
  }, []);

  // ====== Derived numbers
  const nums = useMemo(() => {
    const objActuel = toNumberLoose(objectifBrutActuel, 58000);
    const fixe = toNumberLoose(salaireFixeBrut, 48000);
    const mois = clamp(Math.round(toNumberLoose(moisPresence, 12)), 1, 12);

    const caPrev = toNumberLoose(caN1, 0);
    const objN = toNumberLoose(objectifAnneeN, 1500000);

    const sal = clamp(toNumberLoose(tauxChargesSalariales, 22), 0, 60);
    const pat = clamp(toNumberLoose(tauxChargesPatronales, 42), 0, 120);
    const ir = clamp(toNumberLoose(tauxIR, 10), 0, 60);

    const avNat = Math.max(0, toNumberLoose(avantagesNature, 0));

    const caEx = Math.max(0, toNumberLoose(caExistants, 0));
    const caNew = Math.max(0, toNumberLoose(caNouveaux, 0));
    const hors = clamp(toNumberLoose(partHorsPerimetre, 0), 0, 100);

    const tauxEx = clamp(toNumberLoose(tauxComExistants, 5), 0, 100);
    const tauxNew = clamp(toNumberLoose(tauxComNouveaux, 10), 0, 100);

    const tMB = clamp(toNumberLoose(tauxMargeBrute, 64), 0, 100);

    const mcModeNormalized = mcMode === "PCT" ? "OVERRIDE_PCT" : mcMode;
    const mcAdj = toNumberLoose(mcAdjEur, 0);
    const mcOver = clamp(toNumberLoose(mcOverridePct, 64), 0, 100);

    const apporteursOn = !!apporteursEnabled;

    // Normalise la liste des apporteurs
    const apPeople = Array.isArray(apporteursPeople) ? apporteursPeople : [];

    // Volume % apporté par les apporteurs (somme des sharePct)
    const apVol = apporteursOn && apPeople.length > 0
      ? apPeople.reduce((sum, p) => sum + toNumberLoose(p.sharePct, 0), 0)
      : 0;

    // Bonus moyen pondéré des apporteurs
    const apBonus = apporteursOn && apPeople.length > 0
      ? apPeople.reduce((sum, p) => {
          const share = toNumberLoose(p.sharePct, 0);
          const rate = toNumberLoose(p.commissionPct, 0);
          return sum + (share * rate);
        }, 0) / Math.max(1, apVol)
      : tauxNew;

    return {
      objActuel,
      fixe,
      mois,
      prorata: mois / 12,
      caPrev,
      objN,
      sal,
      pat,
      ir,
      avNat,
      caEx,
      caNew,
      caNewInScope: caNew * (1 - hors / 100),
      hors,
      tauxEx,
      tauxNew,
      tMB,
      mcMode: mcModeNormalized,
      mcAdj,
      mcOver,
      apporteursOn,
      apVol,
      apBonus,
      apPeople,
    };
  }, [
    objectifBrutActuel,
    salaireFixeBrut,
    moisPresence,
    caN1,
    objectifAnneeN,
    tauxChargesSalariales,
    tauxChargesPatronales,
    tauxIR,
    avantagesNature,
    caExistants,
    caNouveaux,
    partHorsPerimetre,
    tauxComExistants,
    tauxComNouveaux,
    tauxMargeBrute,
    mcMode,
    mcAdjEur,
    mcOverridePct,
    apporteursEnabled,
    apporteursPeople,
  ]);

  
  // ====== Plancher auto : par défaut = Objectif brut (référence) - Salaire fixe brut annuel
  useEffect(() => {
    if (!plancherVarOn) return;
    if (plancherTouchedRef.current) return;
    const obj = toNumberLoose(objectifBrutActuel, 58000);
    const fixe = toNumberLoose(salaireFixeBrut, 48000);
    const autoVal = Math.max(0, Math.round(obj - fixe));
    setPlancherVarVal(String(autoVal));
  }, [objectifBrutActuel, salaireFixeBrut, plancherVarOn]);
// ====== Marge effective (pour conversions CA -> MB/MC)
  const effectiveMarginPct = useMemo(() => {
    if (modeCommission === "MC" && nums.mcMode === "OVERRIDE_PCT") return nums.mcOver;
    return nums.tMB;
  }, [modeCommission, nums.mcMode, nums.mcOver, nums.tMB]);

  // ====== Commission base calculator (CA/MB/MC) — clamp à 0
  const commissionBaseFromCA = (ca) => {
    const caSafe = Math.max(0, ca);
    if (modeCommission === "CA") return caSafe;

    const mb = caSafe * (effectiveMarginPct / 100);

    if (modeCommission === "MB") return Math.max(0, mb);

    // MC
    if (nums.mcMode === "EUR") return Math.max(0, mb - nums.mcAdj);
    // OVERRIDE_PCT
    return Math.max(0, caSafe * (nums.mcOver / 100));
  };

  // ====== Prime base volume
  const primeBaseVolume = useMemo(() => {
    const caTotalEst =
      nums.objN > 0 ? nums.objN : Math.max(0, nums.caPrev + nums.caNewInScope);

    if (basePrime === "CA") return caTotalEst;

    const mb = caTotalEst * (effectiveMarginPct / 100);
    if (basePrime === "MB") return Math.max(0, mb);

    // MC
    if (nums.mcMode === "EUR") return Math.max(0, mb - nums.mcAdj);
    return Math.max(0, caTotalEst * (nums.mcOver / 100));
  }, [basePrime, nums, effectiveMarginPct]);

  // ====== Seuil 1 suggéré (info)
  const seuil1Default = useMemo(() => {
    const coutEmployeurFixeAnnuel = nums.fixe * (1 + nums.pat / 100);
    const coutFixeProrata = coutEmployeurFixeAnnuel * nums.prorata;
    const caN1Prorata = nums.caPrev * nums.prorata;
    return Math.max(0, caN1Prorata + coutFixeProrata);
  }, [nums.fixe, nums.pat, nums.prorata, nums.caPrev]);

  useEffect(() => {
    // Vue OBJECTIFS : triggerStart auto si non touché
    if (primeView !== "OBJECTIFS") return;
    if (triggerTouchedRef.current) return;
    if (String(primeTriggerStart || "").trim() !== "") return;
    setPrimeTriggerStart(String(Math.round(seuil1Default)));
  }, [seuil1Default, primeView, primeTriggerStart]);

  // ====== Prime annual
  const primeBrutAnnuel = useMemo(() => {
    if (!primeEnabled) return 0;

    if (primeView === "PLAGES") {
      const { totals } = computePlagesTotals(primeRanges, lastEndUserValue);
      return totals.primeAmount;
    }

    // OBJECTIFS
    const trig = toNumberLoose(primeTriggerStart, seuil1Default);
    const objectives = (primeObjectives || [])
      .filter((o) => (o.enabled ?? true) && (o.reached ?? true))
      .map((o) => ({
        objective: toNumberLoose(o.objective, 0),
        ratePct: toNumberLoose(o.ratePct, 0),
      }))
      .filter((o) => Number.isFinite(o.objective) && Number.isFinite(o.ratePct));

    return computePrimeFromObjectives(primeBaseVolume, trig, objectives);
  }, [
    primeEnabled,
    primeView,
    primeRanges,
    lastEndUserValue,
    primeObjectives,
    primeBaseVolume,
    primeTriggerStart,
    seuil1Default,
  ]);

  const { totals: plagesTotals, debugRows: plagesDebugRows } = useMemo(
    () => computePlagesTotals(primeRanges, lastEndUserValue),
    [primeRanges, lastEndUserValue]
  );

  // ====== Commissions brut (avant / après rétrocession)
  const commissions = useMemo(() => {
    const baseEx = commissionBaseFromCA(nums.caEx);
    const baseNew = commissionBaseFromCA(nums.caNewInScope);

    // Clients existants
    const comExBase = baseEx * (nums.tauxEx / 100);

    // Nouveaux clients (avec éventuelle part "apporteurs d'affaires")
    // Nouveau système : calcul basé uniquement sur les apporteurs définis
    
    const apporteursBreakdown = (nums.apporteursOn ? nums.apPeople : [])
      .map((p, idx) => {
        const name = String(p?.name || `Apporteur ${idx + 1}`).trim() || `Apporteur ${idx + 1}`;
        const share = clamp(toNumberLoose(p?.sharePct, 0), 0, 100);
        const rate = clamp(toNumberLoose(p?.ratePct, 0), 0, 100);
        const baseCalc = p?.baseCalc || 'NOUVEAUX';
        
        // Calculer la base selon le choix
        let calcBase = 0;
        if (baseCalc === 'NOUVEAUX') {
          calcBase = baseNew;
        } else if (baseCalc === 'EXISTANTS') {
          calcBase = baseEx;
        } else if (baseCalc === 'TOUS') {
          calcBase = baseEx + baseNew;
        }
        
        // L'apporteur touche son taux sur sa part
        const amount = Math.max(0, calcBase) * (share / 100) * (rate / 100);
        
        return { 
          id: p?.id || String(idx), 
          name, 
          sharePct: share, 
          ratePct: rate, 
          baseCalc,
          amount 
        };
      })
      .filter((x) => x.amount > 0 || (x.sharePct > 0 && x.ratePct > 0));

    const totalApporteurs = apporteursBreakdown.reduce((a, b) => a + (b.amount || 0), 0);

    // Calculer la commission du directeur sur les nouveaux clients
    let comNew = 0;
    let dirRateNew = nums.tauxNew; // Taux effectif (pour affichage)
    
    if (nums.apporteursOn && apporteursBreakdown.length > 0) {
      // Calculer la part totale apportée sur NOUVEAUX uniquement
      const totalShareOnNew = apporteursBreakdown
        .filter(a => a.baseCalc === 'NOUVEAUX' || a.baseCalc === 'TOUS')
        .reduce((sum, a) => {
          if (a.baseCalc === 'NOUVEAUX') {
            return sum + a.sharePct;
          } else if (a.baseCalc === 'TOUS') {
            // Part proportionnelle sur nouveaux
            const proportionNew = baseNew / (baseEx + baseNew || 1);
            return sum + (a.sharePct * proportionNew);
          }
          return sum;
        }, 0);
      
      const clampedShare = Math.min(totalShareOnNew, 100);
      const directShare = 100 - clampedShare; // Part que j'apporte moi-même
      
      // Commission sur ma part directe (taux plein)
      comNew += baseNew * (directShare / 100) * (nums.tauxNew / 100);
      
      // Commission sur la part apportée (taux réduit = tauxNew - tauxApporteur)
      let weightedRateSum = directShare * nums.tauxNew;
      
      apporteursBreakdown.forEach(a => {
        if (a.baseCalc === 'NOUVEAUX') {
          const reducedRate = Math.max(0, nums.tauxNew - a.ratePct);
          comNew += baseNew * (a.sharePct / 100) * (reducedRate / 100);
          weightedRateSum += a.sharePct * reducedRate;
        } else if (a.baseCalc === 'TOUS') {
          // Part proportionnelle sur nouveaux
          const proportionNew = baseNew / (baseEx + baseNew || 1);
          const effectiveShareOnNew = a.sharePct * proportionNew;
          const reducedRate = Math.max(0, nums.tauxNew - a.ratePct);
          comNew += baseNew * (effectiveShareOnNew / 100) * (reducedRate / 100);
          weightedRateSum += effectiveShareOnNew * reducedRate;
        }
      });
      
      // Taux effectif moyen (pour affichage)
      dirRateNew = weightedRateSum / 100;
    } else {
      // Pas d'apporteurs : taux plein sur tout
      comNew = baseNew * (nums.tauxNew / 100);
      dirRateNew = nums.tauxNew;
    }
    const totalAvant = comExBase + comNew;
    const totalApres = totalAvant;

    // Calcul de la base apporteurs (pour affichage/debug)
    const apporteursBase = baseNew;

    return {
      baseEx,
      baseNew,

      comExBase,
      comNew,
      dirRateNew,

      apporteursBase,
      apporteursBreakdown,
      totalApporteurs,

      totalAvant,
      totalApres,
    };
  }, [nums, modeCommission, effectiveMarginPct]);

  // ====== Totals (annual + period)
  const totals = useMemo(() => {
    const brutAvNat = inclureAvantagesDansObjectif ? nums.avNat : 0;

    const variableCalc = commissions.totalApres + primeBrutAnnuel;
    const variableAnnuel = plancherVarOn
      ? Math.max(toNumberLoose(plancherVarVal, 0), variableCalc)
      : variableCalc;

    const brutAnnuel = nums.fixe + variableAnnuel + brutAvNat;
    const brutPeriode = brutAnnuel * nums.prorata;

    const netAnnuelAvantIR = brutAnnuel * (1 - nums.sal / 100);
    const netAnnuel = netAnnuelAvantIR * (1 - nums.ir / 100);

    const netPeriodeAvantIR = brutPeriode * (1 - nums.sal / 100);
    const netPeriode = netPeriodeAvantIR * (1 - nums.ir / 100);

    const coutEmployeurFixeAnnuel = nums.fixe * (1 + nums.pat / 100);
    const coutEmployeurTotalAnnuel = brutAnnuel * (1 + nums.pat / 100);
    const coutEmployeurFixePeriode = coutEmployeurFixeAnnuel * nums.prorata;
    const coutEmployeurTotalPeriode = coutEmployeurTotalAnnuel * nums.prorata;

    return {
      brutAnnuel,
      brutPeriode,
      netAnnuel,
      netPeriode,
      coutEmployeurFixeAnnuel,
      coutEmployeurTotalAnnuel,
      coutEmployeurFixePeriode,
      coutEmployeurTotalPeriode,
      brutAvNat,
      variableAnnuel,
      variableCalc,
    };
  }, [
    nums,
    commissions,
    primeBrutAnnuel,
    inclureAvantagesDansObjectif,
    plancherVarOn,
    plancherVarVal,
  ]);

  // ====== Target check (58k)
  const compareTarget = useMemo(() => {
    const target = nums.objActuel;
    const actual =
      compareMode === "PERIODE" ? totals.brutPeriode : totals.brutAnnuel;
    const delta = actual - target;
    return { target, actual, delta, ok: delta >= 0 };
  }, [nums.objActuel, totals, compareMode]);

  // ====== Coefficient demandé (delta / 10 000) :contentReference[oaicite:19]{index=19}
  const coeffObjectif = useMemo(() => {
    const c = compareTarget.delta / 10000;
    // exemple demandé : 6000 -> 0.6 ; -3000 -> -0.3
    return Math.round(c * 10) / 10;
  }, [compareTarget.delta]);

  // ====== Seuil de rentabilité - CA nouveaux clients nécessaire
  const breakEven = useMemo(() => {
    const target = nums.objActuel;

    const computeBrutCompared = (caNewAnnuel) => {
      const caNew = Math.max(0, caNewAnnuel);
      const hors = clamp(toNumberLoose(partHorsPerimetre, 0), 0, 100);
      const caNewInScope = caNew * (1 - hors / 100);

      const baseFromCA = (ca) => {
        const caSafe = Math.max(0, ca);
        if (modeCommission === "CA") return caSafe;

        const mb = caSafe * (effectiveMarginPct / 100);
        if (modeCommission === "MB") return Math.max(0, mb);

        if (nums.mcMode === "EUR") return Math.max(0, mb - nums.mcAdj);
        return Math.max(0, caSafe * (nums.mcOver / 100));
      };

      // Commissions (sans bonus incrément)
      const comEx = baseFromCA(nums.caEx) * (nums.tauxEx / 100);
      const apVol = nums.apporteursOn ? nums.apVol : 0;
      const apBonus = nums.apporteursOn ? nums.apBonus : nums.tauxNew;
      const dirRateNew = ((100 - apVol) * nums.tauxNew + apVol * apBonus) / 100;

      const comNew = baseFromCA(caNewInScope) * (dirRateNew / 100);
      const totalAvant = comEx + comNew;

      const comApres = totalAvant;

      // Prime (selon vue + base prime)
      const caTotalEst =
        nums.objN > 0 ? nums.objN : Math.max(0, nums.caPrev + caNewInScope);

      let baseP = caTotalEst;
      if (basePrime === "MB") baseP = Math.max(0, caTotalEst * (effectiveMarginPct / 100));
      else if (basePrime === "MC") {
        const mb = Math.max(0, caTotalEst * (effectiveMarginPct / 100));
        baseP =
          nums.mcMode === "EUR"
            ? Math.max(0, mb - nums.mcAdj)
            : Math.max(0, caTotalEst * (nums.mcOver / 100));
      }

      let prime = 0;
      if (primeEnabled) {
        if (primeView === "PLAGES") {
          prime = computePlagesTotals(primeRanges, lastEndUserValue).totals.primeAmount;
        } else {
          const trig = toNumberLoose(primeTriggerStart, seuil1Default);
          const objectives = (primeObjectives || [])
            .filter((o) => (o.enabled ?? true) && (o.reached ?? true))
            .map((o) => ({
              objective: toNumberLoose(o.objective, 0),
              ratePct: toNumberLoose(o.ratePct, 0),
            }))
            .filter((o) => Number.isFinite(o.objective) && Number.isFinite(o.ratePct));
          prime = computePrimeFromObjectives(baseP, trig, objectives);
        }
      }

      const brutAvNat = inclureAvantagesDansObjectif ? nums.avNat : 0;

      // Plancher variable : doit s'appliquer aussi pendant le break-even
      const variableCalc = comApres + prime;
      const variableAnnuel = plancherVarOn
        ? Math.max(toNumberLoose(plancherVarVal, 0), variableCalc)
        : variableCalc;

      const brutAnnuel = nums.fixe + variableAnnuel + brutAvNat;
      return compareMode === "PERIODE" ? brutAnnuel * nums.prorata : brutAnnuel;
    };

    const step = 1000;
    const max = 10_000_000;

    const brut0 = computeBrutCompared(0);
    if (brut0 >= target) return { needed: 0, brutAt0: brut0, brutAtNeeded: brut0 };

    let ca = step;
    let brut = brut0;
    for (; ca <= max; ca += step) {
      brut = computeBrutCompared(ca);
      if (brut >= target) break;
    }
    if (ca > max) return { needed: null, brutAt0: brut0, brutAtNeeded: brut };

    let low = ca - step;
    let high = ca;
    for (let i = 0; i < 12; i++) {
      const mid = Math.round((low + high) / 2);
      const b = computeBrutCompared(mid);
      if (b >= target) high = mid;
      else low = mid;
    }
    return {
      needed: high,
      brutAt0: brut0,
      brutAtNeeded: computeBrutCompared(high),
    };
  }, [
    nums,
    partHorsPerimetre,
    basePrime,
    primeEnabled,
    primeView,
    primeRanges,
    lastEndUserValue,
    primeObjectives,
    primeTriggerStart,
    seuil1Default,
    inclureAvantagesDansObjectif,
    compareMode,
    effectiveMarginPct,
    modeCommission,
    plancherVarOn,
    plancherVarVal,
  ]);

  
  // ====== Helpers apporteurs d'affaires
  const addApporteur = () => {
    setApporteursPeople((arr) => {
      const next = Array.isArray(arr) ? [...arr] : [];
      next.push({ id: uid(), name: `Apporteur ${next.length + 1}`, sharePct: "100", ratePct: "5" });
      return next;
    });
  };

  const updateApporteur = (id, patch) => {
    setApporteursPeople((arr) => {
      if (!Array.isArray(arr)) return arr;
      return arr.map((p) => (p.id === id ? { ...p, ...patch } : p));
    });
  };

  const removeApporteur = (id) => {
    setApporteursPeople((arr) => {
      if (!Array.isArray(arr)) return arr;
      if (arr.length <= 1) return arr; // on garde au moins 1 ligne
      return arr.filter((p) => p.id !== id);
    });
  };
// ====== Scenario helpers
  const currentScenarioPayload = () => ({
    id: uid(),
    name: scenarioName?.trim() || `Scénario ${new Date().toLocaleString("fr-FR")}`,
    savedAt: Date.now(),

    objectifBrutActuel,
    salaireFixeBrut,
    moisPresence,
    caN1,
    objectifAnneeN,

    tauxChargesSalariales,
    tauxChargesPatronales,
    tauxIR,

    avantagesNature,
    inclureAvantagesDansObjectif,

    modeCommission,
    tauxMargeBrute,

    mcMode,
    mcAdjEur,
    mcOverridePct,

    caExistants,
    caNouveaux,
    partHorsPerimetre,
    tauxComExistants,
    tauxComNouveaux,

    plancherVarOn,
    plancherVarVal,

    apporteursEnabled,
    apporteursPeople,

    primeEnabled,
    basePrime,
    primeView,
    primeTriggerStart,
    lastEndUserValue,
    primeRanges,
    primeObjectives,

    compareMode,
  });

  const applyScenario = (s) => {
    setObjectifBrutActuel(String(s.objectifBrutActuel ?? "58000"));
    setSalaireFixeBrut(String(s.salaireFixeBrut ?? "48000"));
    setMoisPresence(String(s.moisPresence ?? "12"));
    setCaN1(String(s.caN1 ?? "1030000"));
    setObjectifAnneeN(String(s.objectifAnneeN ?? "1500000"));

    setTauxChargesSalariales(String(s.tauxChargesSalariales ?? "22"));
    setTauxChargesPatronales(String(s.tauxChargesPatronales ?? "42"));
    setTauxIR(String(s.tauxIR ?? "10"));

    setAvantagesNature(String(s.avantagesNature ?? "0"));
    setInclureAvantagesDansObjectif(!!s.inclureAvantagesDansObjectif);

    setModeCommission(String(s.modeCommission ?? "MB"));
    setTauxMargeBrute(String(s.tauxMargeBrute ?? "64"));

    const mm = String(s.mcMode ?? "EUR");
    setMcMode(mm === "PCT" ? "OVERRIDE_PCT" : mm);
    setMcAdjEur(String(s.mcAdjEur ?? "0"));
    setMcOverridePct(String(s.mcOverridePct ?? "64"));

    setCaExistants(String(s.caExistants ?? "1000000"));
    setCaNouveaux(String(s.caNouveaux ?? "300000"));
    setPartHorsPerimetre(String(s.partHorsPerimetre ?? "0"));
    setTauxComExistants(String(s.tauxComExistants ?? "5"));
    setTauxComNouveaux(String(s.tauxComNouveaux ?? "10"));

    setPlancherVarOn(s.plancherVarOn ?? true);
    setPlancherVarVal(String(s.plancherVarVal ?? "10000"));
    plancherTouchedRef.current = s.plancherVarVal !== undefined && s.plancherVarVal !== null;
    setApporteursEnabled(!!s.apporteursEnabled);
    setApporteursPeople(Array.isArray(s.apporteursPeople) && s.apporteursPeople.length ? s.apporteursPeople : [{ id: uid(), name: "Apporteur 1", sharePct: "100", ratePct: "5", baseCalc: "NOUVEAUX" }]);

    setPrimeEnabled(!!s.primeEnabled);
    setBasePrime(String(s.basePrime ?? "CA"));
    setPrimeView(String(s.primeView ?? "PLAGES"));

    // trigger start
    const trig = String(s.primeTriggerStart ?? "");
    setPrimeTriggerStart(trig);
    triggerTouchedRef.current = trig.trim() !== "";
    setLastEndUserValue(String(s.lastEndUserValue ?? ""));

    // Ranges/Objectives (avec fallback / compat)
    setPrimeRanges(
      Array.isArray(s.primeRanges)
        ? s.primeRanges.map((r) => ({
            id: r.id || uid(),
            start: String(r.start ?? ""),
            ratePct: String(r.ratePct ?? "0"),
            enabled: r.enabled ?? true,
            reached: r.reached ?? true,
          }))
        : [
            { id: uid(), start: "1500000", ratePct: "0.5", enabled: true, reached: true },
            { id: uid(), start: "1600001", ratePct: "1", enabled: true, reached: true },
            { id: uid(), start: "1800000", ratePct: "1.5", enabled: true, reached: false },
          ]
    );
    setPrimeObjectives(Array.isArray(s.primeObjectives) ? s.primeObjectives : []);

    setCompareMode(String(s.compareMode ?? "ANNUEL"));
  };

  const leftScenario = useMemo(
    () => scenarios.find((s) => s.id === compareLeftId) || null,
    [scenarios, compareLeftId]
  );
  const rightScenario = useMemo(
    () => scenarios.find((s) => s.id === compareRightId) || null,
    [scenarios, compareRightId]
  );

  // Résumé scénario (pour comparaison)
  const computeSummaryFromScenario = (s) => {
    const objActuel = toNumberLoose(s.objectifBrutActuel, 58000);
    const fixe = toNumberLoose(s.salaireFixeBrut, 48000);
    const mois = clamp(Math.round(toNumberLoose(s.moisPresence, 12)), 1, 12);
    const prorata = mois / 12;

    const sal = clamp(toNumberLoose(s.tauxChargesSalariales, 22), 0, 60);
    const pat = clamp(toNumberLoose(s.tauxChargesPatronales, 42), 0, 120);
    const ir = clamp(toNumberLoose(s.tauxIR, 10), 0, 60);

    const caPrev = toNumberLoose(s.caN1, 0);
    const objN = toNumberLoose(s.objectifAnneeN, 1500000);

    const caEx = Math.max(0, toNumberLoose(s.caExistants, 0));
    const caNew = Math.max(0, toNumberLoose(s.caNouveaux, 0));
    const hors = clamp(toNumberLoose(s.partHorsPerimetre, 0), 0, 100);
    const caNewInScope = caNew * (1 - hors / 100);

    const mode = s.modeCommission || "MB";
    const tMB = clamp(toNumberLoose(s.tauxMargeBrute, 64), 0, 100);

    const mcModeLocal = String(s.mcMode ?? "EUR");
    const mcModeNorm = mcModeLocal === "PCT" ? "OVERRIDE_PCT" : mcModeLocal;
    const mcAdj = toNumberLoose(s.mcAdjEur, 0);
    const mcOver = clamp(toNumberLoose(s.mcOverridePct, 64), 0, 100);

    const effectivePct =
      mode === "MC" && mcModeNorm === "OVERRIDE_PCT" ? mcOver : tMB;

    const baseFromCA = (ca) => {
      const caSafe = Math.max(0, ca);
      if (mode === "CA") return caSafe;
      const mb = caSafe * (effectivePct / 100);
      if (mode === "MB") return Math.max(0, mb);
      if (mcModeNorm === "EUR") return Math.max(0, mb - mcAdj);
      return Math.max(0, caSafe * (mcOver / 100));
    };

    const tauxEx = clamp(toNumberLoose(s.tauxComExistants, 5), 0, 100);
    const tauxNew = clamp(toNumberLoose(s.tauxComNouveaux, 10), 0, 100);

    // Calculer les bases pour commissions
    const baseEx = baseFromCA(caEx);
    const baseNew = baseFromCA(caNewInScope);
    
    const comEx = baseEx * (tauxEx / 100);

    // Apporteurs d'affaires (nouveaux)
    const apporteursOn = !!s.apporteursEnabled;
    const apPeople = Array.isArray(s.apporteursPeople) ? s.apporteursPeople : [];
    
    // Calculer apVol et apBonus depuis les données du scénario
    const apVol = apporteursOn && apPeople.length > 0
      ? apPeople.reduce((sum, p) => sum + toNumberLoose(p.sharePct, 0), 0)
      : 0;
    
    const apBonus = apporteursOn && apPeople.length > 0
      ? apPeople.reduce((sum, p) => {
          const share = toNumberLoose(p.sharePct, 0);
          const rate = toNumberLoose(p.commissionPct, 0);
          return sum + (share * rate);
        }, 0) / Math.max(1, apVol)
      : tauxNew;

    const dirRateNew = ((100 - apVol) * tauxNew + apVol * apBonus) / 100;
    const comNew = baseNew * (dirRateNew / 100);

    const apporteursBase = baseNew * (apVol / 100);
    const apporteursBreakdown = apporteursOn
      ? apPeople.map((p, idx) => {
          const name = String(p?.name || `Apporteur ${idx + 1}`).trim() || `Apporteur ${idx + 1}`;
          const share = clamp(toNumberLoose(p?.sharePct, 0), 0, 100);
          const rate = clamp(toNumberLoose(p?.ratePct, 0), 0, 100);
          const amount = Math.max(0, apporteursBase) * (share / 100) * (rate / 100);
          return { name, amount };
        })
      : [];
    const totalApporteurs = apporteursBreakdown.reduce((a, b) => a + (b.amount || 0), 0);

    const totalAvant = comEx + comNew;

    // idem calcul live : TA commission est déjà réduite par le bonus sur la part "apporteurs"
    const comApres = totalAvant;

    // Prime
    const avNat = Math.max(0, toNumberLoose(s.avantagesNature, 0));
    const includeAv = !!s.inclureAvantagesDansObjectif;
    const brutAv = includeAv ? avNat : 0;

    const pEnabled = !!s.primeEnabled;
    const bPrime = s.basePrime || "CA";
    const pView = s.primeView || "PLAGES";

    const caTotalEst = objN > 0 ? objN : Math.max(0, caPrev + caNewInScope);

    let primeBase = caTotalEst;
    if (bPrime === "MB") {
      primeBase = Math.max(0, caTotalEst * (effectivePct / 100));
    } else if (bPrime === "MC") {
      const mb = Math.max(0, caTotalEst * (effectivePct / 100));
      primeBase =
        mcModeNorm === "EUR"
          ? Math.max(0, mb - mcAdj)
          : Math.max(0, caTotalEst * (mcOver / 100));
    }

    // seuil1 (pour trigger default)
    const coutEmployeurFixeAnnuel = fixe * (1 + pat / 100);
    const seuil1 = Math.max(0, caPrev * prorata + coutEmployeurFixeAnnuel * prorata);

    let prime = 0;
    if (pEnabled) {
      if (pView === "PLAGES") {
        prime = computePlagesTotals(s.primeRanges, s.lastEndUserValue).totals.primeAmount;
      } else {
        const trig = toNumberLoose(s.primeTriggerStart, seuil1);
        const objectives = Array.isArray(s.primeObjectives)
          ? s.primeObjectives
              .filter((o) => (o.enabled ?? true) && (o.reached ?? true))
              .map((o) => ({
                objective: toNumberLoose(o.objective, 0),
                ratePct: toNumberLoose(o.ratePct, 0),
              }))
              .filter((o) => Number.isFinite(o.objective) && Number.isFinite(o.ratePct))
          : [];
        prime = computePrimeFromObjectives(primeBase, trig, objectives);
      }
    }

    // Plancher variable
    const pOn = s.plancherVarOn ?? true;
    const pVal = toNumberLoose(s.plancherVarVal, 10000);
    const variableCalc = comApres + prime;
    const variableAnnuel = pOn ? Math.max(pVal, variableCalc) : variableCalc;

    const brutAnnuel = fixe + variableAnnuel + brutAv;
    const brutPeriode = brutAnnuel * prorata;

    const netAnnuel = brutAnnuel * (1 - sal / 100) * (1 - ir / 100);
    const netPeriode = brutPeriode * (1 - sal / 100) * (1 - ir / 100);

    const coutEmployeur = brutAnnuel * (1 + pat / 100);
    const coutEmployeurPeriode = coutEmployeur * prorata;

    const compareModeLocal = s.compareMode || "ANNUEL";
    const actual = compareModeLocal === "PERIODE" ? brutPeriode : brutAnnuel;
    const ok = actual >= objActuel;

    return {
      brutAnnuel,
      brutPeriode,
      netAnnuel,
      netPeriode,
      coutEmployeur,
      coutEmployeurPeriode,
      variableAnnuel: comApres,
      primeAnnuel: prime,
      apporteursAnnuel: totalApporteurs,
      ok,
      actual,
      target: objActuel,
      prorata,
    };
  };

  const leftSummary = useMemo(
    () => (leftScenario ? computeSummaryFromScenario(leftScenario) : null),
    [leftScenario]
  );
  const rightSummary = useMemo(
    () => (rightScenario ? computeSummaryFromScenario(rightScenario) : null),
    [rightScenario]
  );

  // ====== CSS fixes:
  const globalCss = `
    select option {
      color: #111 !important;
      background: #fff !important;
    }
  `;

  const printCss = `
    @media print {
      body { background: #ffffff !important; }
      .noPrint { display: none !important; }
      .printArea { color: #111 !important; background: #fff !important; }
    }
  `;

  // ====== Handler impression
  const handlePrint = () => {
    const canCompare = !!(printCompare && leftScenario && rightScenario);
    prevTabRef.current = tab;
    setPrintMode(canCompare ? "COMPARE" : "DEFAULT");
    setTab("PRINT");
    enqueueTelemetry("export_pdf", {
      sessionId: sessionIdRef.current,
      mode: canCompare ? "COMPARE" : "DEFAULT",
      fromTab: tab
    });

    // Laisse React rendre la vue PRINT avant de lancer window.print()
    setTimeout(() => {
      window.print();
    }, 50);
  };

  useEffect(() => {
    const onAfter = () => {
      setTab(prevTabRef.current || "FIXE");
      setPrintMode(null);
    };
    window.addEventListener("afterprint", onAfter);
    return () => window.removeEventListener("afterprint", onAfter);
  }, []);


  // ====== Bandeau objectif
  const bandeau = (
    <div
      style={{
        ...styles.banner,
        ...(compareTarget.ok ? styles.bannerOk : styles.bannerBad),
      }}
    >
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 10,
          flexWrap: "wrap",
        }}
      >
        <Badge tone={compareTarget.ok ? "ok" : "bad"}>
          {compareTarget.ok ? "🟢 OK" : "🔴 NON"}
        </Badge>

        <div style={{ fontWeight: 950, fontSize: 16 }}>
          Objectif {fmtEur(compareTarget.target)} atteint ?
          <span
            style={{
              opacity: 0.9,
              marginLeft: 10,
              fontWeight: 800,
              fontSize: 13,
            }}
          >
            ({compareMode === "PERIODE" ? "projection sur temps de présence" : "projection annuelle"})
          </span>
        </div>

        <div style={{ marginLeft: "auto" }} className="noPrint">
          <button
            type="button"
            onClick={() => setBannerOpen((v) => !v)}
            style={styles.smallBtn}
          >
            {bannerOpen ? "Masquer détails" : "Afficher détails"}
          </button>
        </div>
      </div>

      {bannerOpen ? (
        <>
          <div style={{ marginTop: 10, opacity: 0.95 }}>
            Total brut comparé : <b>{fmtEur(compareTarget.actual)}</b> — Écart :{" "}
            <b>{fmtEur(compareTarget.delta)}</b>
          </div>

          <div style={{ marginTop: 10, display: "flex", gap: 10, flexWrap: "wrap" }} className="noPrint">
            <button
              type="button"
              onClick={() => setCompareMode(compareMode === "PERIODE" ? "ANNUEL" : "PERIODE")}
              style={styles.smallBtn}
            >
              Comparer : {compareMode === "ANNUEL" ? "Projection annuelle" : "Projection sur présence"}
            </button>

            <button type="button" onClick={handlePrint} style={styles.smallBtn}>
              Imprimer / Exporter PDF
            </button>
          </div>
        </>
      ) : null}
    </div>
  );

  // Helper d’affichage MC/MB sous le menu
  const renderModeParams = () => {
    if (modeCommission === "CA") {
      return (
        <div style={{ opacity: 0.85, lineHeight: 1.4 }}>
          Aucun réglage de marge requis en mode <b>Sur CA</b>.
        </div>
      );
    }

    if (modeCommission === "MB") {
      return (
        <Field
          label="Taux de marge brute standardisé"
          hint="Champ modifiable. Par défaut : 64%."
        >
          <NumInput value={tauxMargeBrute} onChange={setTauxMargeBrute} suffix="%" />
        </Field>
      );
    }

    // MC
    return (
      <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
        <Field
          label="Taux de marge brute standardisé (référence)"
          hint="Utilisé si tu choisis l’ajustement en € (coûts directs)."
        >
          <NumInput
            value={tauxMargeBrute}
            onChange={setTauxMargeBrute}
            suffix="%"
            disabled={mcMode === "OVERRIDE_PCT"}
          />
        </Field>

        <Field
          label="Mode Marge contributive"
          hint="Soit un ajustement en € (coûts directs) qui diminue la marge, soit un % qui remplace le taux standard (64% par défaut)."
        >
          <div style={{ display: "flex", gap: 10, flexWrap: "wrap", alignItems: "center" }}>
            <Select
              value={mcMode}
              onChange={setMcMode}
              options={[
                { value: "EUR", label: "Ajustement en € (coûts directs)" },
                { value: "OVERRIDE_PCT", label: "Remplacer le taux de marge (en %)" },
              ]}
            />
            {mcMode === "EUR" ? (
              <NumInput value={mcAdjEur} onChange={setMcAdjEur} suffix="€" />
            ) : (
              <NumInput value={mcOverridePct} onChange={setMcOverridePct} suffix="%" />
            )}
          </div>
        </Field>

        <div
          style={{
            padding: 12,
            borderRadius: 14,
            background: "rgba(0,0,0,0.22)",
            border: "1px solid rgba(255,255,255,0.08)",
          }}
        >
          <div style={{ fontWeight: 900, marginBottom: 6 }}>Marge effective utilisée</div>
          <div style={{ opacity: 0.9 }}>
            {mcMode === "OVERRIDE_PCT" ? (
              <>
                Taux effectif : <b>{fmtPct(nums.mcOver, 2)}</b> (remplace le 64%).
              </>
            ) : (
              <>
                Taux effectif : <b>{fmtPct(nums.tMB, 2)}</b> — Ajustement coûts directs :{" "}
                <b>{fmtEur(nums.mcAdj)}</b>.
              </>
            )}
          </div>
        </div>
      </div>
    );
  };

  return (
    <div style={styles.page} className="printArea">
      <style>{printCss}</style>
      <style>{globalCss}</style>

      <div style={styles.container}>
        <div
          data-plages-engine={PLAGES_ENGINE_SIGNATURE}
          data-plages-debug-flag={DEBUG_PLAGES_SIGNATURE}
          style={{ display: "none" }}
        />
        <div style={styles.header}>
          <div>
            <div style={styles.h1}>Simulateur de rémunération</div>
            <div style={styles.h2}>
              Fixe + variable + rétrocession + prime sur objectifs + scénarios
            </div>

            {/* ✅ Coefficient demandé, en haut de page :contentReference[oaicite:20]{index=20} */}
            <div style={{ marginTop: 10, display: "flex", gap: 10, flexWrap: "wrap", alignItems: "center" }}>
              <Badge tone={coeffObjectif >= 0 ? "ok" : "bad"}>
                Coefficient objectif : {fmtSigned(coeffObjectif, 1)}
              </Badge>
</div>
          </div>

          <div className="noPrint">
            <Badge tone="neutral">v3</Badge>
          </div>
        </div>

        {bandeau}

        <Tabs
          value={tab}
          onChange={setTab}
          tabs={[
            { value: "FIXE", label: "Fixe" }, // ✅ :contentReference[oaicite:21]{index=21}
            { value: "VARIABLE", label: "Variable" },
            { value: "PRIME", label: "Prime sur objectifs" },
            { value: "RECAP", label: "Récapitulatif" },
            { value: "SCENARIOS", label: "Scénarios" },
          ]}
        />

        {/* ===== FIXE ===== */}
        {tab === "FIXE" ? (
          <Card title="Fixe" subtitle="Réglages de référence + prorata + charges">
            <div style={styles.grid2}>
              <Field label="Objectif brut (référence)" hint="Ex : 58 000 €">
                <NumInput
                  value={objectifBrutActuel}
                  onChange={(v) => { setObjectifBrutActuel(v); trackField("objectifBrutActuel", v); }}
                  suffix="€"
                />
              </Field>

              <Field label="Salaire fixe brut annuel" hint="Par défaut : 48 000 €">
                <NumInput
                  value={salaireFixeBrut}
                  onChange={(v) => { setSalaireFixeBrut(v); trackField("salaireFixeBrut", v); }}
                  suffix="€"
                />
              </Field>

              <Field
                label="Nombre de mois de présence"
                hint="Ex : 8 si prise de poste début mai."
              >
                <NumInput value={moisPresence} onChange={(v) => { setMoisPresence(v); trackField("moisPresence", v); }} suffix="mois" />
              </Field>

              <Field
                label="CA N-1"
                hint="Référence historique (utile pour seuil 1 de prime)."
              >
                <NumInput value={caN1} onChange={setCaN1} suffix="€" />
              </Field>

              <Field
                label="Objectif année N (optionnel)"
                hint="Si renseigné, sert de base CA total pour la prime sur objectifs."
              >
                <NumInput value={objectifAnneeN} onChange={(v) => { setObjectifAnneeN(v); trackField("objectifAnneeN", v); }} suffix="€" />
              </Field>

              <Field label="Avantages en nature (estimation brute)">
                <NumInput value={avantagesNature} onChange={setAvantagesNature} suffix="€" />
                <div style={{ marginTop: 10 }}>
                  <Toggle
                    checked={inclureAvantagesDansObjectif}
                    onChange={setInclureAvantagesDansObjectif}
                    label="Inclure ces avantages dans le total comparé à l’objectif"
                  />
                </div>
              </Field>
            </div>

            <Divider />

            <div style={styles.grid3}>
              <Field
                label="Charges salariales"
                hint="Approche simplifiée brut → net"
              >
                <NumInput
                  value={tauxChargesSalariales}
                  onChange={setTauxChargesSalariales}
                  suffix="%"
                />
              </Field>
              <Field
                label="Charges patronales"
                hint="Approche simplifiée pour coût employeur"
              >
                <NumInput
                  value={tauxChargesPatronales}
                  onChange={setTauxChargesPatronales}
                  suffix="%"
                />
              </Field>
              <Field label="Impôt sur le revenu (IR)">
                <NumInput value={tauxIR} onChange={setTauxIR} suffix="%" />
              </Field>
            </div>

            <Divider />

            <Table
              rows={[
                { k: "Prorata (mois/12)", v: fmtPct(nums.prorata * 100, 2) },
                {
                  k: "Coût employeur (fixe seul) — annuel",
                  v: fmtEur(totals.coutEmployeurFixeAnnuel),
                },
                {
                  k: "Coût employeur (fixe seul) — sur présence",
                  v: fmtEur(totals.coutEmployeurFixePeriode),
                },
              ]}
            />
          </Card>
        ) : null}

        {/* ===== VARIABLE ===== */}
        {tab === "VARIABLE" ? (
          <Card
            title="Variable"
            subtitle="Commissions clients existants + nouveaux clients, hors périmètre, rétrocession, et base (CA / Marge brute / Marge contributive)."
          >
            {/* ✅ Plancher déplacé ici :contentReference[oaicite:22]{index=22} */}
            <Card
              title="Plancher de rémunération variable"
              subtitle="Optionnel : garantit un minimum annuel de variable (commissions + prime sur objectifs)."
            >
              <Toggle
                checked={plancherVarOn}
                onChange={(v) => { setPlancherVarOn(v); trackToggle("plancherVarOn", v); }}
                label="Activer le plancher"
              />
              <div style={{ marginTop: 10, maxWidth: 360 }}>
                <Field label="Montant plancher annuel" hint="Par défaut : 10 000 €">
                  <NumInput value={plancherVarVal} onChange={(v) => { plancherTouchedRef.current = true; setPlancherVarVal(v); }} suffix="€" />
                </Field>
              </div>
            </Card>

            <Divider />

            {/* Top: mode + paramètres */}
            <div style={styles.grid2}>
              <Field label="Mode de calcul de la commission">
                <Select
                  value={modeCommission}
                  onChange={(v) => { setModeCommission(v); trackToggle("modeCommission", v); }}
                  options={[
                    { value: "CA", label: "Sur CA" },
                    { value: "MB", label: "Sur Marge brute" },
                    { value: "MC", label: "Sur Marge contributive" },
                  ]}
                />
              </Field>

              <div>{renderModeParams()}</div>
            </div>

            <Divider />

            <div style={styles.grid2}>
              <Field
                label="Part hors périmètre sur CA nouveaux clients"
                hint="% du CA nouveaux clients amenés hors scope → pas de commission pour toi."
              >
                <NumInput
                  value={partHorsPerimetre}
                  onChange={setPartHorsPerimetre}
                  suffix="%"
                />
              </Field>

              <Field
                label="CA annuel nouveaux clients"
                hint={`Après hors périmètre : ${fmtEur(nums.caNewInScope)}.`}
              >
                <NumInput value={caNouveaux} onChange={(v) => { setCaNouveaux(v); trackField("caNouveaux", v); }} suffix="€" />
                <div style={{ marginTop: 10 }}>
                  <div style={styles.sliderRow}>
                    <input
                      type="range"
                      min="0"
                      max={sliderMax}
                      step={sliderStep}
                      value={sliderCaNew}
                      onChange={(e) => setCaNouveaux(String(e.target.value))}
                      style={{ width: "100%" }}
                    />
                    <div style={styles.sliderVal}>{fmtEur(sliderCaNew)}</div>
                  </div>
                  <div style={styles.hint}>Slider et champ sont liés.</div>
                </div>
              </Field>

              <Field
                label="CA clients existants (sur une année)"
                hint="Base du variable sur portefeuille existant."
              >
                <NumInput value={caExistants} onChange={(v) => { setCaExistants(v); trackField("caExistants", v); }} suffix="€" />
              </Field>

              <Field label="Taux commission nouveaux clients" hint="Ex : 7% sur nouveaux clients.">
                <NumInput value={tauxComNouveaux} onChange={(v) => { setTauxComNouveaux(v); trackField("tauxComNouveaux", v); }} suffix="%" />
              </Field>

              <Field label="Taux commission clients existants" hint="Ex : 5% sur clients existants (taux de base).">
                <NumInput value={tauxComExistants} onChange={(v) => { setTauxComExistants(v); trackField("tauxComExistants", v); }} suffix="%" />
              </Field>
            </div>

            <Divider />

            <Toggle
              checked={apporteursEnabled}
              onChange={(v) => { setApporteursEnabled(v); trackToggle("apporteursEnabled", v); }}
              label="Bonus apporteurs d'affaires"
              hint="Si une partie du CA nouveaux est apportée par d'autres personnes, tu ne prends qu'un bonus sur cette part et on calcule ce que les apporteurs empochent."
            />

            {apporteursEnabled ? (
              <div style={{ marginTop: 12 }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
                  <div style={{ fontWeight: 900 }}>Apporteurs d'affaires</div>
                  <button type="button" onClick={addApporteur} style={styles.smallBtn}>
                    + Ajouter une personne
                  </button>
                </div>

                <div style={{ marginTop: 10, display: "flex", flexDirection: "column", gap: 10 }}>
                  {(apporteursPeople || []).map((p, idx) => (
                    <div key={p.id} style={{ ...styles.rowBox, padding: 12 }}>
                      <div style={styles.grid4}>
                        <Field label={`Nom (apporteur ${idx + 1})`}>
                          <input value={p.name} onChange={(e) => updateApporteur(p.id, { name: e.target.value })} style={styles.input} placeholder="Ex : NICO" dir="ltr" />
                        </Field>

                        <Field label="Part du CA apporté" hint="% de la part apporteurs attribuée à cette personne.">
                          <NumInput
                            value={p.sharePct}
                            onChange={(v) => updateApporteur(p.id, { sharePct: v })}
                            suffix="%"
                          />
                        </Field>

                        <Field label="Taux de commission" hint="% que l'apporteur empoche sur sa part.">
                          <NumInput
                            value={p.ratePct}
                            onChange={(v) => updateApporteur(p.id, { ratePct: v })}
                            suffix="%"
                          />
                        </Field>
                        <Field label="Base de calcul" hint="Sur quel CA calculer la commission ?">
                          <select
                            value={p.baseCalc || 'NOUVEAUX'}
                            onChange={(e) => updateApporteur(p.id, { baseCalc: e.target.value })}
                            style={styles.select}
                          >
                            <option value="NOUVEAUX">Nouveaux clients uniquement</option>
                            <option value="EXISTANTS">Clients existants uniquement</option>
                            <option value="TOUS">Tous les clients</option>
                          </select>
                        </Field>

                        <div style={{ display: "flex", alignItems: "flex-end" }}>
                          <button
                            type="button"
                            onClick={() => removeApporteur(p.id)}
                            style={styles.dangerBtn}
                            className="noPrint"
                          >
                            Supprimer
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ) : null}

            <Divider />

            <Table
              rows={[
                { k: "Commission clients existants — base (taux principal)", v: fmtEur(commissions.comExBase) },
                { k: "Commission direction commerciale nouveaux clients — brut (après déduction du montant rétrocédé aux co-apporteurs)", v: `${fmtEur(commissions.comNew)} (taux moyen ${fmtSigned(commissions.dirRateNew, 2)}%)` },
                { k: "Total commission — brut", v: fmtEur(commissions.totalAvant) },
                ...(apporteursEnabled
                  ? [
                      { k: "Total commissions apporteurs d'affaires", v: fmtEur(commissions.totalApporteurs) },
                      ...commissions.apporteursBreakdown.map((p) => ({
                        k: `Commission apporteur d'affaire "${p.name}"`,
                        v: fmtEur(p.amount),
                      })),
                    ]
                  : []),
              ]}/>
          </Card>
        ) : null}

        {/* ===== PRIME ===== */}
        {tab === "PRIME" ? (
          <Card
            title="Prime sur objectifs"
            subtitle="Prime par paliers. Seuil 1 suggéré = (CA N-1 proratisé) + (coût employeur fixe proratisé)."
          >
            <Toggle
              checked={primeEnabled}
              onChange={(v) => { setPrimeEnabled(v); trackToggle("primeEnabled", v); }}
              label="Activer la prime sur objectifs"
            />

            <div style={{ marginTop: 12 }}>
              <div style={styles.grid2}>
                <Field
                  label="Base de la prime"
                  hint="Simplifiée : CA total / Marge brute / Marge contributive"
                >
                  <Select
                    value={basePrime}
                    onChange={(v) => { setBasePrime(v); trackToggle("basePrime", v); }}
                    options={[
                      { value: "CA", label: "CA total" },
                      { value: "MB", label: "Marge brute" },
                      { value: "MC", label: "Marge contributive" },
                    ]}
                  />
                </Field>

                <Field
                  label="Seuil 1 suggéré (info)"
                  hint="Calcul : (CA N-1 × prorata) + (coût employeur fixe × prorata)"
                >
                  <div style={{ fontWeight: 900 }}>{fmtEur(seuil1Default)}</div>
                </Field>
              </div>

              <Divider />

              <div style={styles.grid2}>
                <Field
                  label="Vue d'affichage des paliers"
                  hint="Deux modes : Plages (début/fin) ou Objectifs (objectif + taux sur volume)."
                >
                  <Select
                    value={primeView}
                    onChange={(v) => { setPrimeView(v); trackToggle("primeView", v); }}
                    options={[
                      { value: "PLAGES", label: "Plages de CA" },
                      { value: "OBJECTIFS", label: "Objectifs CA" },
                    ]}
                  />
                </Field>

                {primeView === "OBJECTIFS" ? (
                  <Field
                    label="Déclenchement du palier 1 à partir de"
                    hint="Par défaut : Seuil 1 suggéré. Tant que la base prime est en dessous, aucun palier ne s'applique."
                  >
                    <NumInput
                      value={primeTriggerStart}
                      onChange={(v) => {
                        triggerTouchedRef.current = true;
                        setPrimeTriggerStart(v);
                      }}
                      suffix="€"
                      placeholder={String(Math.round(seuil1Default))}
                    />
                  </Field>
                ) : (
                  <div />
                )}
              </div>

              <Divider />

              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
                <div style={{ fontWeight: 900 }}>Paliers</div>
                <button
                  type="button"
                  onClick={() => {
                    if (primeView === "PLAGES") {
                      setPrimeRanges((arr) => {
                        const prev = Array.isArray(arr) && arr.length > 0 ? arr[arr.length - 1] : null;
                        const startFromPrev = prev ? String(Math.max(0, toNumberLoose(prev.start, 0) + 1)) : "";
                        return [
                          ...(Array.isArray(arr) ? arr : []),
                          { id: uid(), start: startFromPrev, ratePct: "0", enabled: true, reached: false },
                        ];
                      });
                    } else {
                      setPrimeObjectives((arr) => [
                        ...arr,
                        { id: uid(), objective: "", ratePct: "0.0", enabled: true, reached: true },
                      ]);
                    }
                  }}
                  style={styles.smallBtn}
                  className="noPrint"
                >
                  + Ajouter un palier
                </button>
              </div>

              <div style={{ marginTop: 12 }}>
                {primeView === "PLAGES" ? (
                  primeRanges
                    .map((t, idx) => {
                      const isLast = idx === primeRanges.length - 1;
                      const nextStart = !isLast ? Math.max(0, toNumberLoose(primeRanges[idx + 1]?.start, 0)) : null;
                      const derivedEnd = isLast ? null : nextStart - 1;
                      const lastEndDisplay = String(lastEndUserValue ?? "").trim() === "" ? "∞" : String(lastEndUserValue);
                      return (
                      <div key={t.id} style={styles.tierRow}>
                        <div style={{ flex: 1 }}>
                          <Field label={`Palier ${idx + 1} — Début`}>
                            <NumInput
                              value={t.start}
                              onChange={(v) => {
                                setPrimeRanges((arr) => {
                                  if (!Array.isArray(arr)) return arr;
                                  const next = arr.map((x) => ({ ...x }));
                                  const i = next.findIndex((x) => x.id === t.id);
                                  if (i < 0) return arr;

                                  const raw = String(v ?? "");
                                  next[i].start = raw;

                                  return next;
                                });
                              }}
                              onBlur={() => {
                                setPrimeRanges((arr) => {
                                  if (!Array.isArray(arr)) return arr;
                                  const next = arr.map((x) => ({ ...x }));
                                  const i = next.findIndex((x) => x.id === t.id);
                                  if (i < 0) return arr;

                                  const parsed = toNumberLoose(next[i].start, Number.NaN);
                                  if (!Number.isFinite(parsed)) return arr;

                                  const startNum = Math.max(0, parsed);

                                  const isLast = i === next.length - 1;

                                  if (i > 0) {
                                    const prevStart = Math.max(0, toNumberLoose(next[i - 1].start, 0));
                                    if (startNum <= prevStart) {
                                      alert("Le début du palier doit être strictement supérieur au palier précédent");
                                      return arr;
                                    }
                                  }

                                  if (!isLast) {
                                    const nextStartVal = Math.max(0, toNumberLoose(next[i + 1].start, 0));
                                    if (startNum >= nextStartVal) {
                                      alert("Le début du palier doit être strictement inférieur au palier suivant");
                                      return arr;
                                    }
                                  }

                                  next[i].start = String(startNum);
                                  return next;
                                });
                              }}
                              suffix="€"
                              placeholder={idx === 0 ? String(Math.round(seuil1Default)) : ""}
                            />
                          </Field>
                        </div>

                        <div style={{ flex: 1 }}>
                          <Field label={`Palier ${idx + 1} — Fin`}>
                            <NumInput
                              value={isLast ? lastEndDisplay : String(derivedEnd)}
                              disabled={!isLast}
                              onChange={(v) => {
                                if (!isLast) return;
                                const raw = String(v ?? "").trim();
                                if (raw === "") {
                                  setLastEndUserValue("");
                                  return;
                                }

                                const startNum = Math.max(0, toNumberLoose(t.start, 0));
                                const endNum = toNumberLoose(raw, Number.NaN);
                                if (!Number.isFinite(endNum) || endNum <= startNum) {
                                  alert("La valeur de fin doit être strictement supérieure au début");
                                  return;
                                }
                                setLastEndUserValue(String(endNum));
                              }}
                              suffix="€"
                              placeholder="∞"
                            />
                          </Field>
                        </div>

                        <div style={{ width: 220 }}>
                          <Field label="Taux sur tranche">
                            <NumInput
                              value={t.ratePct}
                              onChange={(v) =>
                                setPrimeRanges((arr) =>
                                  arr.map((x) => (x.id === t.id ? { ...x, ratePct: v } : x))
                                )
                              }
                              suffix="%"
                            />
                          </Field>
                        </div>

                        <div style={{ width: 170 }}>
                          <Field label="Actif">
                            <Toggle
                              checked={t.enabled ?? true}
                              onChange={(v) =>
                                setPrimeRanges((arr) =>
                                  arr.map((x) => (x.id === t.id ? { ...x, enabled: v } : x))
                                )
                              }
                              label={(t.enabled ?? true) ? "Oui" : "Non"}
                            />
                          </Field>
                        </div>

                        <div style={{ width: 170 }}>
                          <Field label="Atteint">
                            <Toggle
                              checked={t.reached ?? true}
                              onChange={(v) =>
                                setPrimeRanges((arr) =>
                                  arr.map((x) => (x.id === t.id ? { ...x, reached: v } : x))
                                )
                              }
                              label={(t.reached ?? true) ? "Oui" : "Non"}
                            />
                          </Field>
                        </div>

                        <div className="noPrint">
                          <button
                            type="button"
                            onClick={() =>
                              setPrimeRanges((arr) => arr.filter((x) => x.id !== t.id))
                            }
                            style={styles.dangerBtn}
                          >
                            Supprimer
                          </button>
                        </div>
                      </div>
                    )})
                ) : (
                  primeObjectives
                    .slice()
                    .sort((a, b) => toNumberLoose(a.objective, 0) - toNumberLoose(b.objective, 0))
                    .map((t, idx) => (
                      <div key={t.id} style={styles.tierRow}>
                        <div style={{ flex: 1 }}>
                          <Field label={`Palier ${idx + 1} — Objectif CA`}>
                            <NumInput
                              value={t.objective}
                              onChange={(v) =>
                                setPrimeObjectives((arr) =>
                                  arr.map((x) => (x.id === t.id ? { ...x, objective: v } : x))
                                )
                              }
                              suffix="€"
                            />
                          </Field>
                        </div>

                        <div style={{ width: 260 }}>
                          <Field label="Taux sur volume CA">
                            <NumInput
                              value={t.ratePct}
                              onChange={(v) =>
                                setPrimeObjectives((arr) =>
                                  arr.map((x) => (x.id === t.id ? { ...x, ratePct: v } : x))
                                )
                              }
                              suffix="%"
                            />
                          </Field>
                        </div>

                        <div style={{ width: 170 }}>
                          <Field label="Actif">
                            <Toggle
                              checked={t.enabled ?? true}
                              onChange={(v) =>
                                setPrimeObjectives((arr) =>
                                  arr.map((x) => (x.id === t.id ? { ...x, enabled: v } : x))
                                )
                              }
                              label={(t.enabled ?? true) ? "Oui" : "Non"}
                            />
                          </Field>
                        </div>

                        <div style={{ width: 170 }}>
                          <Field label="Atteint">
                            <Toggle
                              checked={t.reached ?? true}
                              onChange={(v) =>
                                setPrimeObjectives((arr) =>
                                  arr.map((x) => (x.id === t.id ? { ...x, reached: v } : x))
                                )
                              }
                              label={(t.reached ?? true) ? "Oui" : "Non"}
                            />
                          </Field>
                        </div>

                        <div className="noPrint">
                          <button
                            type="button"
                            onClick={() =>
                              setPrimeObjectives((arr) => arr.filter((x) => x.id !== t.id))
                            }
                            style={styles.dangerBtn}
                          >
                            Supprimer
                          </button>
                        </div>
                      </div>
                    ))
                )}
              </div>

              <Divider />

              <Table
                rows={[
                  { k: "Base prime (volume)", v: fmtEur(primeView === "PLAGES" ? plagesTotals.baseVolume : primeBaseVolume) },
                  { k: "Prime sur objectifs — brut (annuel)", v: fmtEur(primeBrutAnnuel) },
                ]}
              />

              {DEBUG_PLAGES && primeView === "PLAGES" ? (
                <div style={{ marginTop: 10, ...styles.hint }}>
                  <pre style={{ margin: 0, whiteSpace: "pre-wrap" }}>
                    {JSON.stringify({
                      primeRanges,
                      lastEndUserValue,
                      totals: plagesTotals,
                      debugRows: plagesDebugRows,
                    }, null, 2)}
                  </pre>
                </div>
              ) : null}
            </div>
          </Card>
        ) : null}

        {/* ===== RECAP ===== */}
        {tab === "RECAP" ? (
          <Card
            title="Récapitulatif"
            subtitle="Synthèse (brut / net / coût employeur / variable / prime) + estimation du CA nouveaux clients nécessaire."
            right={
              <div className="noPrint">
                <button type="button" onClick={handlePrint} style={styles.smallBtn}>
                  Imprimer / Exporter PDF
                </button>
              </div>
            }
          >
            <div style={styles.grid2}>
              <Card title={compareMode === "ANNUEL" ? "Projection annuelle" : "Projection sur présence"}>
                <Table
                  rows={[
                    { k: "Total brut", v: fmtEur(compareMode === "ANNUEL" ? totals.brutAnnuel : totals.brutPeriode) },
                    { k: "Total net (après charges + IR)", v: fmtEur(compareMode === "ANNUEL" ? totals.netAnnuel : totals.netPeriode) },
                    { k: "Coût employeur total", v: fmtEur(compareMode === "ANNUEL" ? totals.coutEmployeurTotalAnnuel : totals.coutEmployeurTotalPeriode) },
                    { k: "Dont montant autofinancé (variable + prime) — brut chargé", v: fmtEur(((commissions.totalApres + primeBrutAnnuel) * (1 + nums.pat / 100)) * (compareMode === "ANNUEL" ? 1 : nums.prorata)) },
                    { k: "Variable (commissions après rétrocession)", v: fmtEur(commissions.totalApres * (compareMode === "ANNUEL" ? 1 : nums.prorata)) },
                    { k: "Prime sur objectifs", v: fmtEur(primeBrutAnnuel * (compareMode === "ANNUEL" ? 1 : nums.prorata)) },
                    { k: "Plancher variable appliqué ?", v: plancherVarOn ? `Oui (min ${fmtEur(toNumberLoose(plancherVarVal, 0))})` : "Non" },
                  ]}
                />
              </Card>

              {/* ✅ Plus explicite :contentReference[oaicite:23]{index=23} */}
              <Card
                title="Estimation : CA nouveaux clients nécessaire pour atteindre l’objectif"
                subtitle="Le simulateur fait varier UNIQUEMENT le CA annuel “nouveaux clients” jusqu’à atteindre l’objectif. Tous les autres paramètres restent identiques (fixe, taux, hors périmètre, rétrocession, prime, plancher…)."
              >
                {breakEven.needed === null ? (
                  <div style={{ color: "#ffd6d6" }}>
                    Impossible à atteindre dans la plage testée (0 → 10M€). Vérifie les paramètres.
                  </div>
                ) : (
                  <>
                    <div style={{ fontSize: 28, fontWeight: 950, marginBottom: 8 }}>
                      {fmtEur(breakEven.needed)}
                    </div>
                    <div style={styles.hint}>
                      Mode de comparaison :{" "}
                      <b>{compareMode === "ANNUEL" ? "Projection annuelle" : "Projection sur présence"}</b>
                    </div>

                    <Divider />

                    <div style={{ opacity: 0.9, lineHeight: 1.45, fontSize: 13 }}>
                      <b>Comment lire ce résultat :</b>
                      <ul style={{ margin: "8px 0 0 18px" }}>
                        <li>On part du scénario actuel avec <b>CA nouveaux clients = 0</b>.</li>
                        <li>On augmente le CA nouveaux clients jusqu’à ce que <b>Total brut comparé ≥ Objectif</b>.</li>
                        <li>La rétrocession, la prime et le plancher variable sont <b>pris en compte</b>.</li>
                      </ul>
                    </div>

                    <Divider />

                    <Table
                      rows={[
                        { k: "Total brut si CA nouveaux clients = 0", v: fmtEur(breakEven.brutAt0) },
                        { k: "Total brut au seuil atteint", v: fmtEur(breakEven.brutAtNeeded) },
                      ]}
                    />
                  </>
                )}
              </Card>
            </div>

            <Divider />

            <div style={{ padding: 12, borderRadius: 14, background: "rgba(255,255,255,0.06)" }}>
              <div style={{ fontWeight: 900, marginBottom: 6 }}>Bonus apporteurs d'affaires — état actuel</div>
              <div style={{ opacity: 0.9 }}>
                {apporteursEnabled
                  ? `Activé — Base : ${commissionBaseLabel(modeCommission)} — Apporteurs : ${(apporteursPeople || []).length}`
                  : "Désactivé"}
              </div>
            </div>
          </Card>
        ) : null}

        {/* ===== SCENARIOS ===== */}
        {tab === "SCENARIOS" ? (
          <Card title="Scénarios" subtitle="Sauvegarder / recharger / comparer côte à côte.">
            <div className="noPrint" style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
              <div style={{ minWidth: 260 }}>
                <Field label="Nom du scénario (optionnel)">
                  <NumInput
                    value={scenarioName}
                    onChange={setScenarioName}
                    placeholder="Ex : Offre A / Offre B / Hypothèse..."
                  />
                </Field>
              </div>

              <button
                type="button"
                style={styles.smallBtn}
                onClick={() => {
                  const payload = currentScenarioPayload();
                  setScenarios((arr) => [payload, ...arr]);
                  setScenarioName("");
                  if (!compareLeftId) setCompareLeftId(payload.id);
                  if (!compareRightId && compareLeftId) setCompareRightId(payload.id);
                  enqueueTelemetry("scenario_save", {
                    sessionId: sessionIdRef.current,
                    name: payload.name,
                    scenariosTotal: scenarios.length + 1
                  });
                }}
              >
                Sauvegarder le scénario courant
              </button>

              <label style={{ display: "flex", alignItems: "center", gap: 8, fontWeight: 800 }}>
                <input
                  type="checkbox"
                  checked={printCompare}
                  onChange={(e) => setPrintCompare(e.target.checked)}
                />
                Impression : comparer 2 scénarios
              </label>

              <button type="button" onClick={handlePrint} style={styles.smallBtn}>
                Imprimer / Exporter PDF
              </button>
            </div>

            <Divider />

            <div style={styles.grid2}>
              <Card title="Liste des scénarios" subtitle="Charger / supprimer / sélectionner pour comparer.">
                {scenarios.length === 0 ? (
                  <div style={{ opacity: 0.85 }}>Aucun scénario sauvegardé.</div>
                ) : (
                  <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                    {scenarios.map((s) => (
                      <div key={s.id} style={styles.scRow}>
                        <div style={{ flex: 1 }}>
                          <div style={{ fontWeight: 900 }}>{s.name}</div>
                          <div style={styles.hint}>{new Date(s.savedAt).toLocaleString("fr-FR")}</div>
                        </div>

                        <div className="noPrint" style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                          <button type="button" style={styles.smallBtn} onClick={() => {
                            applyScenario(s);
                            enqueueTelemetry("scenario_load", {
                              sessionId: sessionIdRef.current,
                              name: s.name,
                              savedAt: s.savedAt
                            });
                          }}>
                            Charger
                          </button>
                          <button type="button" style={styles.smallBtn} onClick={() => setCompareLeftId(s.id)}>
                            Comparer (gauche)
                          </button>
                          <button type="button" style={styles.smallBtn} onClick={() => setCompareRightId(s.id)}>
                            Comparer (droite)
                          </button>
                          <button
                            type="button"
                            style={styles.dangerBtn}
                            onClick={() => {
                              enqueueTelemetry("scenario_delete", {
                                sessionId: sessionIdRef.current,
                                name: s.name,
                                scenariosRemaining: scenarios.length - 1
                              });
                              setScenarios((arr) => arr.filter((x) => x.id !== s.id));
                            }}
                          >
                            Supprimer
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </Card>

              <Card title="Comparaison visuelle" subtitle="Total brut / Net / Coût employeur / Variable / Prime (côte à côte)">
                {!leftScenario || !rightScenario ? (
                  <div style={{ opacity: 0.85 }}>Sélectionne 2 scénarios (gauche/droite) pour comparer.</div>
                ) : (
                  <>
                    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
                      <div style={styles.compareHead}>
                        <div style={{ fontWeight: 950 }}>{leftScenario.name}</div>
                        <div style={styles.hint}>
                          {leftSummary?.ok ? <Badge tone="ok">Objectif OK</Badge> : <Badge tone="bad">Objectif NON</Badge>}
                        </div>
                      </div>
                      <div style={styles.compareHead}>
                        <div style={{ fontWeight: 950 }}>{rightScenario.name}</div>
                        <div style={styles.hint}>
                          {rightSummary?.ok ? <Badge tone="ok">Objectif OK</Badge> : <Badge tone="bad">Objectif NON</Badge>}
                        </div>
                      </div>
                    </div>

                    <Divider />

                    <div style={styles.compareTable}>
                      {[
                        { k: "Total brut (annuel)", a: fmtEur(leftSummary.brutAnnuel), b: fmtEur(rightSummary.brutAnnuel) },
                        { k: "Total brut (sur présence)", a: fmtEur(leftSummary.brutPeriode), b: fmtEur(rightSummary.brutPeriode) },
                        { k: "Net (annuel)", a: fmtEur(leftSummary.netAnnuel), b: fmtEur(rightSummary.netAnnuel) },
                        { k: "Net (sur présence)", a: fmtEur(leftSummary.netPeriode), b: fmtEur(rightSummary.netPeriode) },
                        { k: "Coût employeur (annuel)", a: fmtEur(leftSummary.coutEmployeur), b: fmtEur(rightSummary.coutEmployeur) },
                        { k: "Commissions (annuel) après rétrocession", a: fmtEur(leftSummary.variableAnnuel), b: fmtEur(rightSummary.variableAnnuel) },
                        { k: "Prime (annuel)", a: fmtEur(leftSummary.primeAnnuel), b: fmtEur(rightSummary.primeAnnuel) },
                        { k: "Commissions apporteurs (ils empochent, annuel)", a: fmtEur(leftSummary.apporteursAnnuel), b: fmtEur(rightSummary.apporteursAnnuel) },
                        { k: "Écart vs objectif", a: fmtEur(leftSummary.actual - leftSummary.target), b: fmtEur(rightSummary.actual - rightSummary.target) },
                      ].map((r, i) => (
                        <div key={i} style={styles.compareRow}>
                          <div style={styles.compareKey}>{r.k}</div>
                          <div style={styles.compareVal}>{r.a}</div>
                          <div style={styles.compareVal}>{r.b}</div>
                        </div>
                      ))}
                    </div>
                  </>
                )}
              </Card>
            </div>
          </Card>
        ) : null}


        {tab === "PRINT" ? (
          <Card
            title="Export PDF"
            subtitle={
              printMode === "COMPARE"
                ? leftSummary && rightSummary
                  ? "Comparaison de 2 scénarios (gauche / droite) + résumé."
                  : "Aucun scénario sélectionné pour la comparaison."
                : "Résumé de la simulation courante + paramètres."
            }
          >
            {printMode === "COMPARE" && (!leftSummary || !rightSummary) ? (
              <div style={{ padding: 40, textAlign: 'center', opacity: 0.7, fontSize: 16 }}>
                <div style={{ marginBottom: 16, fontSize: 20 }}>⚠️ Aucun scénario sélectionné</div>
                <div>
                  Pour imprimer une comparaison, vous devez :
                  <ol style={{ textAlign: 'left', maxWidth: 500, margin: '16px auto', lineHeight: 1.6 }}>
                    <li>Aller dans l'onglet <strong>Scénarios</strong></li>
                    <li>Sauvegarder au moins <strong>2 scénarios</strong></li>
                    <li>Sélectionner les scénarios à comparer (gauche et droite)</li>
                    <li>Cocher "Impression : comparer 2 scénarios"</li>
                    <li>Cliquer sur "Imprimer / Exporter PDF"</li>
                  </ol>
                </div>
              </div>
            ) : printMode === "COMPARE" && leftSummary && rightSummary ? (
              <>
                <div style={{ fontWeight: 900, marginBottom: 10 }}>
                  Comparaison : {leftScenario?.name || "Scénario gauche"} vs {rightScenario?.name || "Scénario droite"}
                </div>

                <Table
                  rows={[
                    { k: "Brut total (annuel)", a: fmtEur(leftSummary.brutAnnuel), b: fmtEur(rightSummary.brutAnnuel) },
                    { k: "Net total (annuel)", a: fmtEur(leftSummary.netAnnuel), b: fmtEur(rightSummary.netAnnuel) },
                    { k: "Coût employeur (annuel)", a: fmtEur(leftSummary.coutEmployeur), b: fmtEur(rightSummary.coutEmployeur) },
                    { k: "Variable (annuel)", a: fmtEur(leftSummary.variableAnnuel), b: fmtEur(rightSummary.variableAnnuel) },
                    { k: "Prime objectifs (annuel)", a: fmtEur(leftSummary.primeAnnuel), b: fmtEur(rightSummary.primeAnnuel) },
                    { k: "Commissions apporteurs (ils empochent, annuel)", a: fmtEur(leftSummary.apporteursAnnuel), b: fmtEur(rightSummary.apporteursAnnuel) },
                    { k: "Objectif brut (référence)", a: fmtEur(leftSummary.target), b: fmtEur(rightSummary.target) },
                    { k: "Objectif atteint ?", a: leftSummary.ok ? "✅" : "❌", b: rightSummary.ok ? "✅" : "❌" },
                    { k: "Écart vs objectif", a: fmtEur(leftSummary.actual - leftSummary.target), b: fmtEur(rightSummary.actual - rightSummary.target) },
                  ]}
                />

                <Divider />

                <div style={{ fontWeight: 900, marginBottom: 10 }}>Paramètres (extrait)</div>

                <Table
                  rows={[
                    { k: "Fixe brut annuel", a: fmtEur(toNumberLoose(leftScenario?.salaireFixeBrut, 48000)), b: fmtEur(toNumberLoose(rightScenario?.salaireFixeBrut, 48000)) },
                    { k: "Mois de présence", a: String(leftScenario?.moisPresence ?? "12"), b: String(rightScenario?.moisPresence ?? "12") },
                    { k: "Mode commission", a: commissionModeLabel(leftScenario?.modeCommission || "MB"), b: commissionModeLabel(rightScenario?.modeCommission || "MB") },
                    { k: "CA existants", a: fmtEur(toNumberLoose(leftScenario?.caExistants, 0)), b: fmtEur(toNumberLoose(rightScenario?.caExistants, 0)) },
                    { k: "CA nouveaux", a: fmtEur(toNumberLoose(leftScenario?.caNouveaux, 0)), b: fmtEur(toNumberLoose(rightScenario?.caNouveaux, 0)) },
                    { k: "Taux nouveaux", a: `${fmtSigned(toNumberLoose(leftScenario?.tauxComNouveaux, 10), 1)}%`, b: `${fmtSigned(toNumberLoose(rightScenario?.tauxComNouveaux, 10), 1)}%` },
                    { k: "Plancher variable", a: leftScenario?.plancherVarOn ? fmtEur(toNumberLoose(leftScenario?.plancherVarVal, 0)) : "OFF", b: rightScenario?.plancherVarOn ? fmtEur(toNumberLoose(rightScenario?.plancherVarVal, 0)) : "OFF" },
                    { k: "Bonus apporteurs", a: leftScenario?.apporteursEnabled ? `ON (${(leftScenario?.apporteursPeople || []).length} apporteurs)` : "OFF", b: rightScenario?.apporteursEnabled ? `ON (${(rightScenario?.apporteursPeople || []).length} apporteurs)` : "OFF" },
                    { k: "Prime objectifs", a: leftScenario?.primeEnabled ? "ON" : "OFF", b: rightScenario?.primeEnabled ? "ON" : "OFF" },
                  ]}
                />
              </>
            ) : (
              <>
                <div style={{ marginBottom: 10, fontWeight: 900 }}>Résumé</div>
                <Table
                  rows={[
                    { k: "Objectif brut (référence)", v: fmtEur(nums.objActuel) },
                    { k: "Fixe brut annuel", v: fmtEur(nums.fixe) },
                    { k: "Mois de présence", v: `${nums.mois} / 12 (${fmtSigned(nums.prorata * 100, 1)}%)` },
                    { k: "Total brut", v: fmtEur(compareMode === "ANNUEL" ? totals.brutAnnuel : totals.brutPeriode) },
                    { k: "Total net (après charges + IR)", v: fmtEur(compareMode === "ANNUEL" ? totals.netAnnuel : totals.netPeriode) },
                    { k: "Coût employeur total", v: fmtEur(compareMode === "ANNUEL" ? totals.coutEmployeurTotalAnnuel : totals.coutEmployeurTotalPeriode) },
                    { k: "Dont montant autofinancé (variable + prime) — brut chargé", v: fmtEur(((commissions.totalApres + primeBrutAnnuel) * (1 + nums.pat / 100)) * (compareMode === "ANNUEL" ? 1 : nums.prorata)) },
                    { k: "Variable (commissions)", v: fmtEur(commissions.totalApres * (compareMode === "ANNUEL" ? 1 : nums.prorata)) },
                    { k: "Prime sur objectifs", v: fmtEur(primeBrutAnnuel * (compareMode === "ANNUEL" ? 1 : nums.prorata)) },
                    { k: "Plancher variable appliqué ?", v: plancherVarOn ? `ON (${fmtEur(nums.plancherVar)})` : "OFF" },
                  ]}
                />

                <Divider />

                <div style={{ marginBottom: 10, fontWeight: 900 }}>Paramètres complets</div>
                <Table
                  rows={[
                    { k: "Mode commission", v: commissionModeLabel(modeCommission) },
                    { k: "Taux marge brute", v: `${fmtSigned(nums.tMB, 1)}%` },
                    { k: "CA existants", v: fmtEur(nums.caEx) },
                    { k: "CA nouveaux (in scope)", v: fmtEur(nums.caNewInScope) },
                    { k: "Part hors périmètre", v: `${fmtSigned(nums.hors, 1)}%` },
                    { k: "Taux commission existants", v: `${fmtSigned(nums.tauxEx, 1)}%` },
                    { k: "Taux commission nouveaux", v: `${fmtSigned(nums.tauxNew, 1)}%` },
                    { k: "Avantages en nature (annuel)", v: fmtEur(nums.avNat) },
                    { k: "Taux charges salariales", v: `${fmtSigned(nums.sal, 1)}%` },
                    { k: "Taux charges patronales", v: `${fmtSigned(nums.pat, 1)}%` },
                    { k: "Taux impôt sur le revenu", v: `${fmtSigned(nums.ir, 1)}%` },
                    { k: "Bonus apporteurs", v: apporteursEnabled ? `ON — ${(apporteursPeople || []).length} apporteur(s)` : "OFF" },
                    { k: "Prime sur objectifs", v: primeEnabled ? `ON — vue ${primeView === "PLAGES" ? "Plages" : "Objectifs"}` : "OFF" },
                  ]}
                />

                {primeEnabled && primeView === "PLAGES" && (primeRanges || []).length > 0 && (
                  <>
                    <Divider />
                    <div style={{ marginBottom: 10, fontWeight: 900 }}>Détails prime - Plages</div>
                    <Table
                      rows={plagesDebugRows
                        .filter((r) => r.enabled ?? true)
                        .map((r, idx) => ({
                          k: `Plage ${idx + 1}`,
                          v: `${fmtEur(r.start)} - ${r.end == null ? "∞" : fmtEur(r.end)} → ${fmtSigned(r.ratePct, 1)}%`
                        }))}
                    />
                  </>
                )}

                {primeEnabled && primeView === "OBJECTIFS" && (primeObjectives || []).length > 0 && (
                  <>
                    <Divider />
                    <div style={{ marginBottom: 10, fontWeight: 900 }}>Détails prime - Objectifs</div>
                    <Table
                      rows={(primeObjectives || [])
                        .filter((o) => o.enabled ?? true)
                        .map((o, idx) => ({
                          k: `Objectif ${idx + 1}`,
                          v: `${fmtEur(toNumberLoose(o.objective, 0))} → ${fmtSigned(toNumberLoose(o.ratePct, 0), 1)}%`
                        }))}
                    />
                  </>
                )}

                {apporteursEnabled && (apporteursPeople || []).length > 0 && (
                  <>
                    <Divider />
                    <div style={{ marginBottom: 10, fontWeight: 900 }}>Détails apporteurs</div>
                    <Table
                      rows={(apporteursPeople || []).map((p, idx) => ({
                        k: p.name || `Apporteur ${idx + 1}`,
                        v: `Part: ${fmtSigned(toNumberLoose(p.sharePct, 0), 1)}% — Taux: ${fmtSigned(toNumberLoose(p.commissionPct, 0), 1)}% — Base: ${p.baseCalc || 'NOUVEAUX'}`
                      }))}
                    />
                  </>
                )}
              </>
            )}
          </Card>
        ) : null}

        <div style={{ height: 30 }} />
      </div>
    </div>
  );
}

/* =========================
   Styles
========================= */

const styles = {
  page: {
    minHeight: "100vh",
    background:
      "radial-gradient(1200px 800px at 20% 10%, rgba(102, 140, 255, 0.22), transparent 55%)," +
      "radial-gradient(1000px 700px at 90% 20%, rgba(0, 255, 209, 0.10), transparent 60%)," +
      "linear-gradient(180deg, #081027 0%, #050914 60%, #040611 100%)",
    color: "#eaf0ff",
    padding: "26px 16px",
  },
  container: { maxWidth: 1120, margin: "0 auto" },
  header: {
    display: "flex",
    justifyContent: "space-between",
    gap: 16,
    alignItems: "flex-start",
    marginBottom: 14,
  },
  h1: { fontSize: 28, fontWeight: 950, letterSpacing: 0.2 },
  h2: { marginTop: 6, opacity: 0.82 },

  card: {
    background: "rgba(255,255,255,0.06)",
    border: "1px solid rgba(255,255,255,0.10)",
    borderRadius: 18,
    padding: 16,
    boxShadow: "0 14px 40px rgba(0,0,0,0.35)",
    backdropFilter: "blur(10px)",
    marginTop: 14,
  },
  cardHeader: {
    display: "flex",
    justifyContent: "space-between",
    gap: 12,
    alignItems: "flex-start",
    marginBottom: 12,
  },
  cardTitle: { fontSize: 18, fontWeight: 950 },
  cardSubtitle: { marginTop: 4, opacity: 0.8, fontSize: 13 },

  tabsRow: { display: "flex", gap: 8, flexWrap: "wrap", marginTop: 12 },
  tabBtn: {
    padding: "10px 12px",
    borderRadius: 999,
    border: "1px solid rgba(255,255,255,0.14)",
    background: "rgba(255,255,255,0.04)",
    color: "#eaf0ff",
    cursor: "pointer",
    fontWeight: 800,
  },
  tabBtnActive: {
    background: "rgba(102, 140, 255, 0.22)",
    border: "1px solid rgba(102, 140, 255, 0.50)",
  },

  banner: {
    marginTop: 12,
    padding: 14,
    borderRadius: 16,
    border: "1px solid rgba(255,255,255,0.14)",
    background: "rgba(255,255,255,0.06)",
  },
  bannerOk: {
    border: "1px solid rgba(60, 255, 177, 0.35)",
    background: "rgba(60, 255, 177, 0.08)",
  },
  bannerBad: {
    border: "1px solid rgba(255, 92, 92, 0.35)",
    background: "rgba(255, 92, 92, 0.08)",
  },

  badge: {
    padding: "6px 10px",
    borderRadius: 999,
    fontWeight: 900,
    fontSize: 12,
    border: "1px solid rgba(255,255,255,0.18)",
  },
  badgeOk: { background: "rgba(60, 255, 177, 0.14)" },
  badgeBad: { background: "rgba(255, 92, 92, 0.14)" },
  badgeWarn: { background: "rgba(255, 217, 102, 0.16)" },
  badgeNeutral: { background: "rgba(255,255,255,0.08)" },

  grid2: { display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 },
  grid3: { display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 12 },

  field: {
    padding: 12,
    borderRadius: 16,
    background: "rgba(0,0,0,0.22)",
    border: "1px solid rgba(255,255,255,0.08)",
  },
  fieldLabel: { fontWeight: 900 },
  hint: { marginTop: 6, opacity: 0.8, fontSize: 12, lineHeight: 1.35 },

  inputWrap: { position: "relative", display: "flex", alignItems: "center" },
  input: {
    width: "100%",
    padding: "12px 44px 12px 12px",
    borderRadius: 14,
    border: "1px solid rgba(255,255,255,0.12)",
    background: "rgba(255,255,255,0.06)",
    color: "#eaf0ff",
    outline: "none",
    fontWeight: 800,
  },
  inputDisabled: {
    cursor: "not-allowed",
    background: "rgba(255,255,255,0.04)",
  },
  suffix: { position: "absolute", right: 12, opacity: 0.85, fontWeight: 900 },

  select: {
    width: "100%",
    padding: 12,
    borderRadius: 14,
    border: "1px solid rgba(255,255,255,0.12)",
    background: "rgba(255,255,255,0.06)",
    color: "#eaf0ff",
    outline: "none",
    fontWeight: 800,
  },

  divider: { height: 1, background: "rgba(255,255,255,0.10)", margin: "14px 0" },

  table: {
    borderRadius: 16,
    overflow: "hidden",
    border: "1px solid rgba(255,255,255,0.10)",
    background: "rgba(0,0,0,0.18)",
  },
  tableRow: {
    display: "grid",
    gridTemplateColumns: "1.2fr 1fr",
    gap: 12,
    padding: "10px 12px",
    borderTop: "1px solid rgba(255,255,255,0.08)",
  },
  tableKey: { opacity: 0.86, fontWeight: 800 },
  tableVal: { fontWeight: 950 },

  smallBtn: {
    padding: "10px 12px",
    borderRadius: 14,
    border: "1px solid rgba(255,255,255,0.16)",
    background: "rgba(255,255,255,0.06)",
    color: "#eaf0ff",
    cursor: "pointer",
    fontWeight: 900,
  },
  dangerBtn: {
    padding: "10px 12px",
    borderRadius: 14,
    border: "1px solid rgba(255, 92, 92, 0.35)",
    background: "rgba(255, 92, 92, 0.10)",
    color: "#ffe9e9",
    cursor: "pointer",
    fontWeight: 900,
  },

  toggleRow: { marginTop: 8 },
  toggleLabel: { display: "flex", alignItems: "center", fontWeight: 900 },

  sliderRow: {
    display: "grid",
    gridTemplateColumns: "1fr 160px",
    gap: 12,
    alignItems: "center",
  },
  sliderVal: {
    padding: 10,
    borderRadius: 14,
    border: "1px solid rgba(255,255,255,0.12)",
    background: "rgba(255,255,255,0.06)",
    textAlign: "center",
    fontWeight: 950,
  },

  tierRow: {
    display: "flex",
    gap: 12,
    alignItems: "flex-end",
    flexWrap: "wrap",
    padding: 12,
    borderRadius: 16,
    background: "rgba(0,0,0,0.18)",
    border: "1px solid rgba(255,255,255,0.08)",
    marginBottom: 10,
  },

  scRow: {
    padding: 12,
    borderRadius: 16,
    background: "rgba(0,0,0,0.18)",
    border: "1px solid rgba(255,255,255,0.08)",
    display: "flex",
    gap: 12,
    alignItems: "center",
    flexWrap: "wrap",
  },

  compareHead: {
    padding: 12,
    borderRadius: 16,
    background: "rgba(0,0,0,0.18)",
    border: "1px solid rgba(255,255,255,0.08)",
  },
  compareTable: {
    borderRadius: 16,
    overflow: "hidden",
    border: "1px solid rgba(255,255,255,0.10)",
    background: "rgba(0,0,0,0.18)",
  },
  compareRow: {
    display: "grid",
    gridTemplateColumns: "1.2fr 1fr 1fr",
    gap: 12,
    padding: "10px 12px",
    borderTop: "1px solid rgba(255,255,255,0.08)",
    alignItems: "center",
  },
  compareKey: { opacity: 0.86, fontWeight: 800 },
  compareVal: { fontWeight: 950 },
};
// FORCE REBUILD Tue Feb 10 10:55:21 AM CET 2026
